import '@bn-games/ui-shell/styles.css';
import { playGameOver, playPiecePlace, playChime, setMuted, TurnSignal } from '@bn-games/audio-core';
import { CanvasTooltip, MenuRouter, createMainMenuScreen, setupMenuScale, GameOverlay, AfkTimer, PingButton, openModal, openResumeFailureModal, announce, createGameStateOverlay, el, createAddOpponentControl, type ModalHandle, type ModalAction, type GameOverlayContext, type GameStateOverlayHandle, type GameStateOverlaySnapshot, type GameStateOverlayButtonId, type OpponentIdentity } from '@bn-games/ui-shell';
import { drawPauseGear, gearHitTest, prefersReducedMotion, type GearIconBounds } from '@bn-games/render-helpers';
import { MoveAnnouncer, activeOpenEnds, type AnnouncerMode, type CursorTarget, type NarratedEvent } from './a11y/MoveAnnouncer.js';
import { buildGameStateOverlayLabels } from './a11y/gameStateOverlayLabels.js';
import { KeyboardFocusChain, type FocusItem } from './input/KeyboardFocusChain.js';
import { drawActionFocusRing, drawLeadFocusRing, drawTileFocusRing } from './render/FocusRing.js';
import type { ActionButtonId } from './render/OverlayRenderer.js';
import { createDiscardModal } from '@bn-games/history-core/ui';
import { initDiscordActivity, isDiscordActivity } from '@bn-games/discord-activity';
import {
  ConnectionManager,
  RoomClient,
  SessionStore,
  IDENTITY_ROOM_GRACE_WINDOW_MS,
  readRoomHash,
  resumeRoom,
  type ResumeOutcome,
  type ResumeFailureCause,
} from '@bn-games/room-client';
import { BN_AUTH_CHANGE, fetchMe, getAuthSnapshot, getWsTicket, isLoggedIn } from '@bn-games/auth-client';
import { NetworkGameController } from './network/NetworkGameController.js';
import {
  deserializeChain,
  parseVariant,
  snapshotVariant,
  type ClientMessage,
  type DominoSnapshot,
  type SeatedPlayer,
  type ServerMessage,
} from './network/protocol.js';
import { PendingSyncStore, commitFinishedRecord } from '@bn-games/history-core';
import { ActiveGameStore } from './history/ActiveGameStore.js';
import { GameRecorder, seedRuleFields } from './history/GameRecorder.js';
import { HistoryStore } from './history/HistoryStore.js';
import { syncOnlineRecord } from './history/serverSync.js';
import { seedRuleOverrides } from './history/ReplayController.js';
import { ReplayScene, type ReplayReturnTarget } from './history/ReplayScene.js';
import { recordFromActiveSession } from './history/types.js';
import type { ActiveSession, GameRecord, RecordedMode, RecordedSeed } from './history/types.js';
import { AiPlayer } from './ai/AiPlayer.js';
import { DifficultyStrategy } from './ai/DifficultyStrategy.js';
import { Evaluator } from './ai/Evaluator.js';
import { MoveEnumerator as AiMoveEnumerator } from './ai/MoveEnumerator.js';
import type { AiAction } from './ai/types.js';
import { HardWorkerSearcher } from './ai/worker/HardWorkerSearcher.js';
import {
  applyDraw,
  applyMove,
  applyPass,
  canCurrentPlayerAct,
  claimMuggins,
  createInitialState,
  expireMuggins,
  matchWinnerSeats,
  startNextHand,
  type GameStateData,
} from './game/GameState.js';
import { hasLegalPlay } from './game/MoveValidator.js';
import { seatsFor, type DominoPlayer, type Tile, type Turn } from './game/types.js';
import { applyRuleOverrides, getRuleSet, normalizeVariantId, type VariantId } from './game/variants/index.js';
import type { DominoRuleSet, PlayerCount } from './game/variants/types.js';
import { initI18n, setLocale, t } from './i18n/I18n.js';
import { registerServiceWorker } from './pwa.js';
import { InputHandler, type PointerInteraction, type WheelInteraction } from './input/InputHandler.js';
import { PointerDispatcher, type DispatchLayout } from './input/PointerDispatcher.js';
import { LayoutManager } from './layout/LayoutManager.js';
import type { LayoutConfig } from './layout/types.js';
import {
  computeChainLayout,
  drawChain,
  drawChainRegionRecess,
  drawDropSlotOutline,
  drawTable,
} from './render/BoardRenderer.js';
import { computeChainBounds, regionToRect } from './render/ChainBounds.js';
import { drawDragGhost } from './render/DragGhost.js';
import { leadTileSlot } from './render/geometry.js';
import { rectBounds } from './render/TileRenderer.js';
import {
  drawHandZoneDividers,
  drawHandZoneFrame,
  drawLocalRack,
  drawOpponentStack,
  drawPlayerInfo,
  layoutLocalRack,
  type PlayerInfo,
  type RackTilePlacement,
} from './render/HandRenderer.js';
import {
  drawActionBar,
  drawCurtain,
  drawTurnBanner,
  hitTestActionBar,
  layoutActionBar,
  type ActionButton,
} from './render/OverlayRenderer.js';
import {
  BONEYARD_STACK_MAX_HEIGHT,
  BONEYARD_TO_MUGGINS_GAP,
  drawBoneyardStack,
  hitTestBoneyardStack,
  layoutBoneyardStack,
  type BoneyardStackLayout,
} from './render/BoneyardStack.js';
import { themeToRenderPalette, type RenderPalette } from './render/palette.js';
import { Viewport } from './render/Viewport.js';
import {
  drawResetButton,
  hitTestResetButton,
  layoutResetButton,
  type ViewportControlsButton,
} from './render/ViewportControls.js';
import { SettingsManager } from './settings/SettingsManager.js';
import { roomRuleOptions } from './settings/roomRuleOptions.js';
import { THEMES } from './settings/themes.js';
import type { Settings } from './settings/types.js';
import { createAboutScreen } from './ui/screens/AboutScreen.js';
import { createHistoryListScreen } from './ui/screens/HistoryListScreen.js';
import { createOnlineCreateScreen } from './ui/screens/OnlineCreateScreen.js';
import { createOnlineJoinScreen } from './ui/screens/OnlineJoinScreen.js';
import { createPlayMenuScreen } from './ui/screens/PlayMenuScreen.js';
import { createRulesScreen } from './ui/screens/RulesScreen.js';
import { createSettingsScreen } from './ui/screens/SettingsScreen.js';
import { buildInGameSettingsRows } from './ui/inGameSettings.js';
import { buildResultText } from './ui/resultText.js';
import { isPenaltyScoring, scoreLabelKey, seatDisplayScore } from './ui/scoreDisplay.js';
import { mountSocialDock } from './ui/socialDock.js';
import type { MenuContext, Opponent, Scene } from './ui/types.js';
import {
  AUTOFIT_PADDING,
  AUTOFIT_TWEEN_MS,
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  LEAD_FOCUS_RING_RADIUS,
  WHEEL_ZOOM_STEP,
} from './utils/constants.js';

function setupCanvas(ariaLabel: string): { ctx: CanvasRenderingContext2D; canvas: HTMLCanvasElement } {
  const canvas = document.createElement('canvas');
  // Make the canvas a keyboard target. tabindex=0 so Tab focuses it without
  // overriding the document's natural focus order. The aria-label is the SR
  // user's primer for the keyboard scheme (full narration arrives via the
  // ui-shell LiveRegion once focus + interaction lands).
  canvas.tabIndex = 0;
  canvas.setAttribute('role', 'application');
  canvas.setAttribute('aria-label', ariaLabel);
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('Failed to acquire 2D canvas context');
  return { ctx, canvas };
}

/**
 * Map CSS size × DPR onto the backing bitmap so one unit of the 1280×720
 * logical coordinate system every renderer uses corresponds to the right
 * number of physical pixels.
 *
 * On portrait phones/tablets `layout.shouldRotateBoard` is true: we layer a
 * 90° CW rotation on top of the base scale so the 16:9 table fills the tall
 * viewport. Renderers keep drawing in unrotated CANVAS_WIDTH × CANVAS_HEIGHT
 * coords; the InputHandler inverse-rotates pointer events.
 */
function applyCanvasLayout(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  layout: LayoutConfig,
): void {
  const cssW = layout.displayWidth;
  const cssH = layout.displayHeight;
  const bmW = Math.max(1, Math.round(cssW * layout.devicePixelRatio));
  const bmH = Math.max(1, Math.round(cssH * layout.devicePixelRatio));
  if (canvas.width !== bmW) canvas.width = bmW;
  if (canvas.height !== bmH) canvas.height = bmH;
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;
  // `canvasWidth/canvasHeight` are the post-swap logical dimensions, so
  // sx/sy are uniform (scale × DPR) in both orientations.
  const sx = bmW / layout.canvasWidth;
  const sy = bmH / layout.canvasHeight;
  ctx.setTransform(sx, 0, 0, sy, 0, 0);
  if (layout.shouldRotateBoard) {
    ctx.translate(layout.canvasWidth, 0);
    ctx.rotate(Math.PI / 2);
  }
}

function applyTheme(theme: Settings['theme']): void {
  document.body.setAttribute('data-theme', theme);
}

function applyLayoutAttributes(cfg: LayoutConfig): void {
  document.body.setAttribute('data-layout', cfg.mode);
  document.body.setAttribute('data-orientation', cfg.orientation);
}

interface QueryOverrides {
  readonly playerCount: PlayerCount | null;
  readonly variant: VariantId | null;
  readonly noCurtain: boolean;
}

function readQueryOverrides(): QueryOverrides {
  const p = new URLSearchParams(window.location.search);
  const players = p.get('players');
  const playerCount: PlayerCount | null =
    players === '2' ? 2 : players === '3' ? 3 : players === '4' ? 4 : null;
  const variant: VariantId | null = normalizeVariantId(p.get('variant'));
  return { playerCount, variant, noCurtain: p.has('nocurtain') };
}

type CurtainState =
  | { readonly kind: 'none' }
  | { readonly kind: 'down'; readonly forSeat: number };

/** Minimum delay between AI's notify-turn and the action firing (ms). */
const AI_MIN_THINK_MS = 500;

/**
 * Seats the Discord instance room is created with. Discord gives no reliable
 * headcount at launch — the first player to launch the activity creates the
 * room, and the create-screen seat picker never renders in the iframe — so the
 * table is fixed at head-to-head. A channel where more than two people launch
 * leaves the extras on `ROOM_FULL`: a known limitation being tracked, not an
 * oversight.
 */
const DISCORD_SEAT_COUNT: PlayerCount = 2;

/**
 * The seat that's currently "active" in the engine: the current player, or
 * the Muggins scorer while a pending claim window is open. Used for the
 * turn banner and AI dispatch — independent of which seat's POV the UI
 * surface is rendering.
 */
function actorSeat(state: GameStateData): number {
  if (state.pendingMuggins) return state.pendingMuggins.scorerSeat;
  return state.currentSeat;
}

function seatPositionLabel(seat: DominoPlayer): string {
  return t(`seats.${seat}`);
}

function seatLabelWithIndex(seat: DominoPlayer, index: number): string {
  return `${t('result.seatNumber', { number: index + 1 })} (${seatPositionLabel(seat)})`;
}

// M7c outbox — a finished ONLINE record for a signed-in player is queued
// here and flushed to the server; it never touches `historyStore`. See
// commitRecord() below.
const outbox = new PendingSyncStore<GameRecord>({ storageKey: 'domino-pending-sync' });

async function main(): Promise<void> {
  const overrides = readQueryOverrides();
  const settingsManager = new SettingsManager();
  await initI18n(settingsManager.get().language);
  if (!isDiscordActivity()) registerServiceWorker();
  setupMenuScale();

  applyTheme(settingsManager.get().theme);
  setMuted(!settingsManager.get().sound);

  // "Your turn" cue. Reads the setting per edge so the Settings toggle takes
  // effect without a restart; `setMuted` above still gates the sound itself.
  const turnSignal = new TurnSignal({
    isEnabled: () => settingsManager.get().turnStartSound,
  });

  const { ctx, canvas } = setupCanvas(t('a11y.canvasLabel'));
  const layoutManager = new LayoutManager();
  applyCanvasLayout(canvas, ctx, layoutManager.getConfig());
  applyLayoutAttributes(layoutManager.getConfig());
  layoutManager.onChange((cfg) => {
    applyCanvasLayout(canvas, ctx, cfg);
    applyLayoutAttributes(cfg);
  });

  let palette: RenderPalette = themeToRenderPalette(THEMES[settingsManager.get().theme]);

  let state: GameStateData | null = null;
  let curtain: CurtainState = { kind: 'none' };
  // DOM modal for hand-over / match-over result. Replaces the canvas-drawn
  // result overlay so the Primary (next hand / new match) and Secondary
  // (back to menu) buttons are keyboard- and screen-reader-reachable.
  let resultModal: ModalHandle | null = null;
  // Snapshot of what the modal is currently showing so we can avoid
  // unnecessary DOM rewrites on every render (each one would flash the
  // focused button).
  let resultModalKey: string | null = null;
  // DOM modal surfacing connection-status states (opponent disconnected /
  // reconnecting self / game disconnected) so a network blip doesn't freeze
  // rack-tile input with zero on-screen explanation — mirrors checkers +
  // backgammon's `createGameStateOverlay`. Hand/match-end + rematch stay on
  // `resultModal` above, which is already driven off engine state.
  let gameStateOverlay: GameStateOverlayHandle | null = null;
  let currentLayout: DispatchLayout = {
    rackPlacements: [],
    armEnds: [],
    placedTiles: [],
    chainCenter: { x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2 },
  };
  /**
   * Parallel side-state mirroring `state.chain.arms`. `chainTurns[armIdx][tileIdx]`
   * is the visual fold chosen for the tile at that position. `'straight'` is
   * the default for non-player plays (AI uses `'auto'` so the layout can
   * apply the segment-length fold heuristic).
   */
  let chainTurns: Turn[][] = [];

  function resetChainTurns(): void {
    chainTurns = [];
  }

  /**
   * Sync `chainTurns` with the current chain shape, then append `turn` for
   * the tile just played on `armIndex`. armIndex = -1 means lead — populate
   * each freshly-created arm with one entry for the shared lead tile.
   */
  function recordTurn(armIndex: number, turn: Turn): void {
    if (state === null) return;
    while (chainTurns.length < state.chain.arms.length) chainTurns.push([]);
    if (armIndex === -1) {
      for (let i = 0; i < state.chain.arms.length; i += 1) {
        const tileCount = state.chain.arms[i]?.tiles.length ?? 0;
        const arr = chainTurns[i]!;
        while (arr.length < tileCount) arr.push('straight');
      }
      return;
    }
    // Backfill any tiles that slipped in via spinner formation (perpendicular
    // arms start empty; this is a safety net in case future code adds tiles
    // without going through this path).
    for (let i = 0; i < state.chain.arms.length; i += 1) {
      const arr = chainTurns[i]!;
      const tileCount = state.chain.arms[i]?.tiles.length ?? 0;
      if (i === armIndex) {
        while (arr.length < tileCount - 1) arr.push('straight');
        arr.push(turn);
      } else {
        while (arr.length < tileCount) arr.push('straight');
      }
    }
  }
  let currentActionButtons: readonly ActionButton[] = [];
  let currentBoneyardLayout: BoneyardStackLayout | null = null;
  let currentBoneyardDrawEnabled = false;
  let currentResetButton: ViewportControlsButton = layoutResetButton(
    layoutManager.getConfig().chainRegion,
  );
  let opponent: Opponent = 'local';
  let pendingLocalSeat = 0;
  // Host-only — seat + seat count requested for the next online room create.
  // The server may rotate to honor preferredSeat; seatCount is clamped to
  // [2, 4] server-side. Defaults match the current settings.
  let pendingOnlineSeat = 0;
  let pendingOnlineSeatCount: PlayerCount = 2;
  // Share-link prefill for the manual Join screen. No longer populated from
  // #room=CODE links — those now resolve straight through attemptResume()
  // (see resumeFromHashOrSession) instead of dropping the user on this
  // screen. Left in place (dead after this change) — see T6 summary.
  let pendingJoinCode: string | null = null;
  // Local seat once the server assigns one (GAME_START / RECONNECT_OK). Used
  // to derive `humanSeat` in online mode and to apply per-seat snapshots.
  let localSeat = 0;
  // Variant the server reports for the active online room. Sticky across
  // STATE_SYNC so the engine uses the correct ruleset when rebuilding state.
  let onlineVariant: VariantId | null = null;
  let currentScene: Scene = 'menu';
  const viewport = new Viewport();
  /** Tracks the last chain length we auto-fit against — avoids refitting every frame. */
  let lastFitChainLength = -1;
  /** Last snapshot's hand number — reset viewport only when a new hand starts. */
  let lastSnapshotHandNumber = -1;
  const aiPlayers = new Map<number, AiPlayer>();
  const historyStore = new HistoryStore();
  const recorder = new GameRecorder();

  /**
   * M7c record routing: a signed-in player's finished ONLINE match goes to
   * the outbox + server only, never to `historyStore`; everything else
   * (offline modes, or an anonymous player's online match) is a device
   * record. `isLoggedIn()`'s sticky answer isn't enough here — the outbox
   * needs an account id to stamp the entry with, so this reads the full
   * snapshot instead.
   */
  function commitRecord(record: GameRecord): void {
    const { authState, userId } = getAuthSnapshot();
    commitFinishedRecord({
      record,
      accountId: userId ?? '',
      isOnlineMatch: record.seed.mode === 'online',
      signedIn: authState === 'signed-in',
      saveLocal: (r) => historyStore.add(r),
      syncRemote: syncOnlineRecord,
      outbox,
    });
  }

  /** Retries any queued outbox entries for the current account. No-op when
   * signed out or when the auth answer hasn't resolved yet ('unknown'). */
  function flushOutboxIfSignedIn(): void {
    const { authState, userId } = getAuthSnapshot();
    if (authState !== 'signed-in' || userId === null) return;
    void outbox.flush(userId, syncOnlineRecord);
  }
  let pendingReplay: { record: GameRecord; returnTo: ReplayReturnTarget } | null = null;
  const replayScene = new ReplayScene({
    requestRender: () => {},
    onExit: (target) => navigateTo(target === 'history' ? 'history' : 'menu'),
  });
  // Set by resumeActiveGame() right before navigateTo('game'); navigateTo
  // reads + clears it so startNewGame is skipped when restoring state.
  let resumingActiveGame = false;
  // ─── In-game overlay (gear modal) + AFK hint state ─────────────────
  let gameOverlay: GameOverlay | null = null;
  let afkTimer: AfkTimer | null = null;
  let gearBounds: GearIconBounds | null = null;
  let gearHovered = false;
  const gearTooltip = new CanvasTooltip(canvas);
  // Idle-pointer state: when no pointer is down, hoverPosition tracks the
  // mouse so the render loop can light up the legal drop slots for the tile
  // under the cursor (preview-on-hover, not just on drag).
  let hoverPosition: { x: number; y: number } | null = null;
  const activePointerIds = new Set<number>();
  // Index of the local seat's hand tile to pulse, expiration timestamp.
  let afkHintTileIndex: number | null = null;
  let afkHintExpiresAt = 0;
  let escapeKeyHandler: ((e: KeyboardEvent) => void) | null = null;
  // Tracks whether the current finished hand has already been folded into a
  // GameRecord. Prevents double-write when render passes re-detect handOver
  // before the user clicks "Next Hand" / "New Match".
  let finalizedThisMatch = false;
  // Lazy-init the Hard-tier worker on first use so the menu/idle path doesn't
  // pay for it. Falls back to in-process search if Worker is unavailable.
  let hardWorker: HardWorkerSearcher | null = null;
  function ensureHardWorker(): HardWorkerSearcher | null {
    if (hardWorker !== null) return hardWorker;
    hardWorker = HardWorkerSearcher.tryCreate();
    return hardWorker;
  }

  /**
   * The seat the LOCAL viewer sees through. In hot-seat the surface rotates
   * with the active seat (the engine's actor); in vs-AI / online the human
   * always keeps the south rack regardless of whose engine turn it is.
   */
  function povSeat(state: GameStateData): number {
    if (opponent === 'local') return actorSeat(state);
    return humanSeat(state.playerCount);
  }

  function humanSeat(playerCount: number): number {
    if (opponent === 'online') return Math.min(Math.max(0, localSeat), playerCount - 1);
    return Math.min(Math.max(0, pendingLocalSeat), playerCount - 1);
  }

  // --- Online room setup ---

  const envWsUrl = import.meta.env['VITE_WS_SERVER_URL'] as string | undefined;
  const wsServerUrl = envWsUrl ?? 'ws://localhost:3001/ws/domino';
  const envLobbyWsUrl = import.meta.env['VITE_LOBBY_WS_URL'] as string | undefined;
  const socialDock = mountSocialDock({
    serverUrl: envLobbyWsUrl ?? 'ws://localhost:3001/ws/lobby',
    isGameInProgress: () => currentScene === 'game' && state !== null && !state.matchOver,
  });
  const sessionStore = new SessionStore('domino.online.session', IDENTITY_ROOM_GRACE_WINDOW_MS);
  const connection = new ConnectionManager<ClientMessage, ServerMessage>({
    serverUrl: wsServerUrl,
  });
  // Resolve the WS ticket at connect time, never at screen-mount time: the
  // ticket's TTL is 60s, and the online create/join screens routinely sit open
  // longer than that while the player picks seats or waits for a friend's
  // code. A stale ticket fails verifyWsTicket, the upgrade is accepted as
  // anonymous, and the seat's identity is bound to null for the life of the
  // room — silently costing a signed-in player their verified name, presence,
  // cross-device resume and history association.
  // Discord is deliberately excluded: its ticket comes from the activity
  // handshake and cannot be re-minted from the web session, so that path keeps
  // setTicket() (which the provider would otherwise win over).
  if (!isDiscordActivity()) connection.setTicketProvider(getWsTicket);
  // Resolve the signed-in state once at boot. Two paths reach a live room
  // without ever passing the main menu — the Discord activity bootstrap and
  // hash/session auto-resume — and neither mounts the AuthWidget that is
  // otherwise the only caller of fetchMe(). Without this, isLoggedIn() stays
  // false on those paths and serverSync silently skips recording the match.
  // Chained onto flushOutboxIfSignedIn() above: app start is the one point
  // where a record queued last session (signed in, POST never confirmed)
  // gets retried, and that needs a resolved auth answer first.
  void fetchMe().then(() => flushOutboxIfSignedIn());
  const roomClient = new RoomClient<ClientMessage, ServerMessage>(connection, sessionStore);
  let network: NetworkGameController | null = null;
  // Full room roster, captured once at join time (see roomClient.onUpdate
  // below) so the post-match "add opponent" control can offer every seat,
  // not just the singular `opponent` name used for the turn-banner/AFK
  // wording. Domino has up to three other seats — never a single opponent.
  let onlineRoster: readonly SeatedPlayer[] | null = null;
  // ─── AFK ping (online only) ─────────────────────────────────────────
  let pingButton: PingButton | null = null;
  let pingButtonContainer: HTMLDivElement | null = null;
  let unsubAfkTracker: (() => void) | null = null;

  /**
   * Rebuild a `GameStateData` from a server-authoritative snapshot. Opponent
   * hands arrive as `{ kind: 'hidden', count }` stubs — we pad with placeholder
   * tiles so length is preserved (renderers only read `hand.length` for the
   * opponent stack; the engine isn't run on the client in online mode).
   * Boneyard is replaced by a same-length array of placeholder tiles for the
   * same reason.
   */
  function buildStateFromSnapshot(snapshot: DominoSnapshot): GameStateData {
    const ruleSet: DominoRuleSet = applyRuleOverrides(getRuleSet(snapshotVariant(snapshot)), {
      pointGoal: snapshot.pointGoal,
      mugginsStrict: snapshot.mugginsStrict,
      opening13: snapshot.opening13,
      baldFinish: snapshot.baldFinish,
    });
    const placeholder = (): Tile => [0, 0] as Tile;
    const hands: Tile[][] = snapshot.handsSeatView.map((view) => {
      if (view.kind === 'own') return view.tiles.slice() as Tile[];
      return Array.from({ length: view.count }, placeholder);
    });
    const boneyard: Tile[] = Array.from({ length: snapshot.boneyardCount }, placeholder);
    return {
      ruleSet,
      playerCount: snapshot.playerCount,
      seed: snapshot.seed,
      handNumber: snapshot.handNumber,
      hands,
      boneyard,
      chain: deserializeChain(snapshot.chain),
      currentSeat: snapshot.currentSeat,
      leaderSeat: snapshot.leaderSeat,
      scores: snapshot.scores.slice(),
      teamMap: snapshot.teamMap,
      pendingMuggins: snapshot.pendingMuggins,
      handOver: snapshot.handOver,
      matchOver: snapshot.matchOver,
      lastResult: snapshot.lastResult,
      consecutivePasses: snapshot.consecutivePasses,
      forcedLeadTile: snapshot.forcedLeadTile,
      lastAction: snapshot.lastAction ?? null,
      // Absent on snapshots from a pre-Козёл server during a deploy.
      carriedPenalty: snapshot.carriedPenalty ?? 0,
    };
  }

  function applyOnlineSnapshot(snapshot: DominoSnapshot): void {
    onlineVariant = snapshotVariant(snapshot);
    state = buildStateFromSnapshot(snapshot);
    chainTurns = snapshot.chainTurns.map((arr) => arr.slice() as Turn[]);
    layoutManager.setInputs({
      playerCount: snapshot.playerCount,
      localSeat: humanSeat(snapshot.playerCount),
    });
    dispatcher.reset();
    curtain = { kind: 'none' };
    // Reset framing only on a new hand — every other snapshot (opponent play,
    // draw, pass, muggins claim) preserves the current pan/zoom. The render
    // loop's autofit already re-frames when the chain grows, gated on
    // `userControlled` so a manually panned view stays put.
    if (lastSnapshotHandNumber !== snapshot.handNumber) {
      lastSnapshotHandNumber = snapshot.handNumber;
      lastFitChainLength = -1;
      resetViewportToFit(false);
    }
    refreshKeyboardChain();
    maybeNarrateTurn();
    flushNarrations();
    // Honor autoClaimMuggins in online mode — strict-mode plays park points
    // in pendingMuggins until claimed, and there's no local engine pass to
    // run the auto-claim path that hot-seat / vs-AI use. Without this, a
    // scorer who doesn't tap loses the points when the window expires.
    maybeAutoClaimMugginsOnline(snapshot);
    notifyTurnOwnershipChanged();
  }

  function maybeAutoClaimMugginsOnline(snapshot: DominoSnapshot): void {
    if (network === null) return;
    const pending = snapshot.pendingMuggins;
    if (pending === null) return;
    if (pending.scorerSeat !== localSeat) return;
    if (!settingsManager.get().autoClaimMuggins) return;
    network.sendMugginsClaim();
  }

  function buildNetworkController(): NetworkGameController {
    const controller = new NetworkGameController({
      connection,
      localSeat,
      onSnapshot: (snap) => applyOnlineSnapshot(snap),
      onGameOver: () => {
        // Snapshot already carries matchOver=true, so the result modal renders
        // off the engine state. Invalidate auto-reconnect once the match is
        // over — otherwise a later tab reload auto-rejoins the still-alive
        // room and the rejoiner adopts a stale GAME_OVER snapshot.
        sessionStore.clear();
      },
      onOverlayChange: () => {
        // rAF loop already renders every frame.
      },
      onRematchStart: (seat, variant) => {
        localSeat = seat;
        onlineVariant = (variant !== null ? (parseVariant(variant) as VariantId) : onlineVariant);
        // Server will follow up with a STATE_SYNC carrying the re-shuffled
        // initial state — nothing to do locally beyond updating the seat.
      },
    });
    controller.setOverlayHandlers({
      onResign: (winnerIsLocal) => {
        controller.showGameOver(winnerIsLocal ? 'win' : 'loss');
        gameOverlay?.close();
      },
      onRestartStart: () => {
        ActiveGameStore.clear();
        gameOverlay?.close();
        // Server will follow up with a fresh STATE_SYNC for the new match.
      },
      onRestartStateChange: () => {
        if (gameOverlay?.isOpen()) gameOverlay.refreshRestartState();
      },
    });
    controller.setSettingsGetter(() => settingsManager.get());
    return controller;
  }

  // Subscribe globally so the menu transition lands correctly even if the
  // user switches screens while the room is being created/joined.
  roomClient.onUpdate((info) => {
    if (info.state !== 'ready' || info.localSeat === null) return;
    if (currentScene === 'game') return;
    localSeat = info.localSeat;
    onlineVariant = parseVariant(info.variant ?? undefined);
    if (network) network.detach();
    network = buildNetworkController();
    onlineRoster = info.players;
    const opp = info.players?.find((p) => p.seat !== info.localSeat);
    network.setOpponentName(opp?.name ?? 'Opponent');
    network.attach();
    navigateTo('game');
  });

  roomClient.onReconnectFail(() => {
    // RoomClient's own RECONNECT_FAIL handling already clears this room's
    // stored session (clearFor) — nothing further to clear here, and
    // clearing unconditionally would wipe a different room this client
    // still holds a session for.
  });

  const dispatcher = new PointerDispatcher({
    viewport,
    getLocalHand: () => {
      if (state === null) return [];
      return state.hands[povSeat(state)] ?? [];
    },
    getCurrentChain: () => {
      if (state === null) throw new Error('Dispatcher accessed before game started');
      return state.chain;
    },
    getForcedLeadTile: () => state?.forcedLeadTile ?? null,
    getLayout: () => currentLayout,
    isInputBlocked: () => {
      if (state === null) return true;
      if (opponent === 'online' && network !== null && network.overlay !== 'none') return true;
      return (
        curtain.kind === 'down' ||
        state.handOver ||
        state.pendingMuggins !== null ||
        state.currentSeat !== povSeat(state)
      );
    },
    onCommitPlay: (tile, armIndex, turn) => {
      if (state === null) return;
      if (opponent === 'online') {
        // Server validates + applies; the next STATE_SYNC delivers the result.
        // Reset selection now for immediate UX feedback (server snapshot will
        // reconcile within one RTT).
        network?.sendPlay(tile, armIndex, turn);
        dispatcher.reset();
        playPiecePlace();
        return;
      }
      const moverSeat = state.currentSeat;
      const result = applyMove(state, moverSeat, tile, armIndex, Date.now());
      state = result.state;
      recordTurn(armIndex, turn);
      recorder.recordPlay(moverSeat, tile, armIndex, turn, result.pointsThisPlay);
      playPiecePlace();

      const settings = settingsManager.get();
      if (
        state.pendingMuggins &&
        settings.autoClaimMuggins &&
        state.pendingMuggins.scorerSeat === moverSeat
      ) {
        const claimPoints = state.pendingMuggins.points;
        state = claimMuggins(state, moverSeat, Date.now());
        recorder.recordClaimMuggins(moverSeat, claimPoints);
      }

      afterStateChange(moverSeat);
    },
    onUserControlledViewport: () => viewport.markUserControlled(),
  });

  // ─── A11y: screen-reader narration + keyboard focus chain ─────────────
  // i18next's `t` carries a typed key-literal union; the announcer's translator
  // is generic so unit tests can stub it. Cast at the boundary.
  const tForAnnouncer = (key: string, vars?: Record<string, string | number>): string =>
    t(key as never, vars as never) as unknown as string;
  function announcerMode(): AnnouncerMode {
    if (opponent === 'ai') return 'ai';
    if (opponent === 'online') return 'online';
    return 'local';
  }
  function localSeatForAnnouncer(): number | null {
    if (state === null) return null;
    // Hot-seat: every seat is "local" to whoever's holding the device, so
    // narration uses subject-by-seat-name and never says "you".
    if (opponent === 'local') return null;
    return humanSeat(state.playerCount);
  }
  function announcerSeatLabel(seat: number): string {
    if (state === null) return t('result.seatNumber', { number: seat + 1 });
    const seatName = seatsFor(state.playerCount)[seat] ?? 'south';
    return seatLabelWithIndex(seatName, seat);
  }
  const moveAnnouncer = new MoveAnnouncer(tForAnnouncer, {
    getMode: announcerMode,
    getLocalSeat: localSeatForAnnouncer,
    getSeatLabel: announcerSeatLabel,
  });
  function fireNarration(event: NarratedEvent | null): void {
    if (event === null) return;
    announce(event.text, event.assertive === true ? { assertive: true } : {});
  }

  // Engine-event narration buffer. State-changing operations (play, draw,
  // pass, muggins, turn change, hand-end, match-end) push narration events
  // here and `flushNarrations()` writes them as a single combined announce
  // at the end of the operation. Without this, multiple rapid `announce()`
  // calls overwrite a single polite live-region node — the SR only reads
  // the last write, losing earlier moves/turns.
  const narrationBuffer: { text: string; assertive: boolean }[] = [];
  function pushNarration(event: NarratedEvent | null): void {
    if (event === null) return;
    narrationBuffer.push({ text: event.text, assertive: event.assertive === true });
  }
  function flushNarrations(): void {
    if (narrationBuffer.length === 0) return;
    // Any assertive event lifts the whole combined message to assertive —
    // game-over and muggins-claim shouldn't get buried under a polite move.
    const assertive = narrationBuffer.some((n) => n.assertive);
    const text = narrationBuffer.map((n) => n.text).join(' ');
    narrationBuffer.length = 0;
    announce(text, assertive ? { assertive: true } : {});
  }

  // Tracks the most recent hand/match-end narration we've already fired so
  // the render-loop call to `narrateHandEnd` doesn't spam the SR every frame.
  let narratedHandResultKey: string | null = null;

  const kbdChain = new KeyboardFocusChain(
    {
      getLocalHand: () => {
        if (state === null) return [];
        return state.hands[povSeat(state)] ?? [];
      },
      getChain: () => state?.chain ?? { lead: null, spinner: null, arms: [], activeArms: [] },
      getForcedLeadTile: () => state?.forcedLeadTile ?? null,
      enumerateLegalSlots: (tile) => dispatcher.enumerateLegalSlots(tile),
      canLeadWith: (tile) => dispatcher.canLeadWith(tile),
      getEnabledActions: () => currentEnabledActions,
      isInputBlocked: () => {
        if (state === null) return true;
        if (opponent === 'online' && network !== null && network.overlay !== 'none') return true;
        if (curtain.kind === 'down') return true;
        if (state.handOver) return true;
        // Muggins window: only the scorer has a focusable action; everyone
        // else stays blocked until the window resolves (claim or expire).
        if (state.pendingMuggins !== null) {
          return state.pendingMuggins.scorerSeat !== povSeat(state);
        }
        return state.currentSeat !== povSeat(state);
      },
    },
    {
      onArmTile: () => {
        // Narration handled via the onFocusChange path — the chain immediately
        // moves the cursor to the first armed slot.
      },
      onUnarm: () => {},
      onCommitPlay: (tile, armIndex, turn) => {
        if (state === null) return;
        if (opponent === 'online') {
          // -1 = lead in our chain item; protocol expects -1 as well.
          network?.sendPlay(tile, armIndex, turn);
          dispatcher.reset();
          playPiecePlace();
          return;
        }
        const moverSeat = state.currentSeat;
        const result = applyMove(state, moverSeat, tile, armIndex, Date.now());
        const preMugginsState = result.state;
        state = preMugginsState;
        recordTurn(armIndex, turn);
        recorder.recordPlay(moverSeat, tile, armIndex, turn, result.pointsThisPlay);
        playPiecePlace();
        narratePlay(moverSeat, tile, armIndex, result.pointsThisPlay);

        const settings = settingsManager.get();
        if (
          state.pendingMuggins &&
          settings.autoClaimMuggins &&
          state.pendingMuggins.scorerSeat === moverSeat
        ) {
          const claimPoints = state.pendingMuggins.points;
          state = claimMuggins(state, moverSeat, Date.now());
          recorder.recordClaimMuggins(moverSeat, claimPoints);
          pushNarration(moveAnnouncer.describeMugginsClaim(moverSeat, claimPoints));
        }
        afterStateChange(moverSeat);
      },
      onAction: (action) => {
        if (action === 'draw') performDraw();
        else if (action === 'muggins') performMugginsClaim();
      },
      onFocusChange: (item) => {
        narrateFocusChange(item);
        // Render-loop already redraws every frame, so the focus ring picks
        // up the new item without an explicit invalidation.
      },
      onCurtainKey: () => liftCurtain(),
    },
  );
  let canvasFocused = false;
  // Action buttons available to the keyboard chain — updated each render
  // from the same enable flags the action bar uses.
  let currentEnabledActions: ActionButtonId[] = [];

  function refreshKeyboardChain(): void {
    currentEnabledActions = computeEnabledActions();
    kbdChain.rebuild();
  }

  /**
   * Action buttons the keyboard focus chain should include, mirroring the
   * enable flags the on-canvas action bar derives. Visible-but-disabled
   * (expired Muggins window) is excluded — Tab onto a dead button is a
   * confusing dead end.
   */
  function computeEnabledActions(): ActionButtonId[] {
    if (state === null) return [];
    const s = state;
    if (s.handOver) return [];
    if (curtain.kind === 'down') return [];
    const pov = povSeat(s);
    const result: ActionButtonId[] = [];
    if (s.pendingMuggins !== null && s.pendingMuggins.scorerSeat === pov) {
      const remaining = s.pendingMuggins.expiresAt - Date.now();
      if (remaining > 0) result.push('muggins');
    }
    if (s.pendingMuggins !== null) return result; // hand input is paused
    const seatHand = s.hands[s.currentSeat] ?? [];
    const localIsCurrent = s.currentSeat === pov;
    if (!localIsCurrent) return result;
    const playable = hasLegalPlay(s.chain, seatHand, s.forcedLeadTile);
    if (!playable) {
      if (s.boneyard.length > 0 && s.ruleSet.drawRule !== 'no-draw') {
        result.push('draw');
      }
      // No manual pass — the engine auto-passes when neither play nor
      // draw is possible (see the auto-pass block in render()).
    }
    return result;
  }

  function narratePlay(
    seat: number,
    tile: Tile,
    armIndex: number,
    points: number,
  ): void {
    if (state === null) return;
    const wasLead = armIndex === -1;
    pushNarration(
      moveAnnouncer.describePlay(
        seat,
        tile,
        armIndex,
        wasLead,
        activeOpenEnds(state),
        points,
        state.pendingMuggins,
        Date.now(),
        isPenaltyScoring(state),
      ),
    );
  }

  function cursorTargetFor(item: FocusItem): CursorTarget {
    if (item.kind === 'hand') {
      if (state === null) {
        return { kind: 'hand', tile: item.tile, index: item.tileIndex, count: 1, forcedLead: null, forcedTile: null };
      }
      const hand = state.hands[povSeat(state)] ?? [];
      const forced = state.forcedLeadTile;
      if (forced !== null && state.chain.lead === null && state.currentSeat === povSeat(state)) {
        if (forced[0] === item.tile[0] && forced[1] === item.tile[1]) {
          return {
            kind: 'hand',
            tile: item.tile,
            index: item.tileIndex,
            count: hand.length,
            forcedLead: 'this',
            forcedTile: forced,
          };
        }
        return {
          kind: 'hand',
          tile: item.tile,
          index: item.tileIndex,
          count: hand.length,
          forcedLead: 'other',
          forcedTile: forced,
        };
      }
      return {
        kind: 'hand',
        tile: item.tile,
        index: item.tileIndex,
        count: hand.length,
        forcedLead: null,
        forcedTile: null,
      };
    }
    if (item.kind === 'armSlot') {
      // Scores aren't pre-computed for slots — pass 0 so the narration is the
      // "no score" variant. Compute-on-cursor would require enumerating every
      // candidate's open-ends sum; deferred until requested.
      return {
        kind: 'armSlot',
        tile: item.tile,
        armIndex: item.armIndex,
        fold: item.turn,
        slotIndex: item.slotIndex,
        slotCount: item.slotCount,
        scores: 0,
      };
    }
    return { kind: 'action', action: item.action };
  }

  function narrateFocusChange(item: FocusItem | null): void {
    if (item === null) return;
    fireNarration(moveAnnouncer.describeCursor(cursorTargetFor(item)));
  }

  function maybeNarrateTurn(): void {
    if (state === null) return;
    if (state.handOver) return;
    if (state.pendingMuggins !== null) return;
    pushNarration(moveAnnouncer.describeTurnStart(state.currentSeat));
  }

  function narrateHandEndIfNeeded(): void {
    if (state === null || !state.handOver) {
      narratedHandResultKey = null;
      return;
    }
    const winners = state.lastResult?.winnerSeats ?? [];
    const blocked = state.lastResult?.blocked === true;
    const key = `${state.matchOver ? 'match' : 'hand'}|${state.handNumber}|${winners.join(',')}|${blocked}`;
    if (key === narratedHandResultKey) return;
    pushNarration(moveAnnouncer.describeHandOver(state));
    if (state.matchOver) {
      pushNarration(moveAnnouncer.describeMatchOver(state));
    }
    narratedHandResultKey = key;
    // The result modal opens immediately after this — flush so the SR
    // hears the result before focus jumps to the modal's primary button.
    flushNarrations();
  }

  canvas.addEventListener('focus', () => {
    canvasFocused = true;
    kbdChain.setFocused(true);
  });
  canvas.addEventListener('blur', () => {
    canvasFocused = false;
    kbdChain.setFocused(false);
  });
  canvas.addEventListener('keydown', (e: KeyboardEvent) => {
    if (currentScene !== 'game') return;
    // Escape is owned by the global capture-phase listener (gear overlay).
    // Other keys we consume preventDefault to avoid scrolling.
    const handled = kbdChain.handleKey(e.key);
    if (handled) {
      e.preventDefault();
      e.stopPropagation();
    }
  });

  function performDraw(): void {
    if (state === null) return;
    if (opponent === 'online') {
      network?.sendDraw();
      dispatcher.reset();
      return;
    }
    if (state.boneyard.length === 0) return;
    if (state.ruleSet.drawRule === 'no-draw') return;
    const seat = state.currentSeat;
    state = applyDraw(state, seat);
    recorder.recordDraw(seat);
    dispatcher.reset();
    const handSize = state.hands[seat]?.length ?? 0;
    pushNarration(moveAnnouncer.describeDraw(seat, handSize));
    refreshKeyboardChain();
    // Draw doesn't advance the turn — flush directly (no afterStateChange).
    flushNarrations();
  }

  /**
   * Hit-test the south rack: does (x, y) fall inside any of the local
   * player's tile rects? Returns the placement index or null. Drives the
   * pointer cursor on hover and the hover drop-slot preview.
   */
  function isOverLocalHandTile(x: number, y: number): number | null {
    if (state === null) return null;
    if (state.handOver || state.pendingMuggins !== null) return null;
    if (curtain.kind !== 'none') return null;
    if (state.currentSeat !== povSeat(state)) return null;
    for (const placement of currentLayout.rackPlacements) {
      const { x: rx, y: ry, width, height } = rectBounds(placement.rect);
      if (x >= rx && x <= rx + width && y >= ry && y <= ry + height) {
        return placement.index;
      }
    }
    return null;
  }

  function performMugginsClaim(): void {
    if (state === null || !state.pendingMuggins) return;
    if (opponent === 'online') {
      network?.sendMugginsClaim();
      return;
    }
    const scorer = state.pendingMuggins.scorerSeat;
    const points = state.pendingMuggins.points;
    state = claimMuggins(state, scorer, Date.now());
    recorder.recordClaimMuggins(scorer, points);
    pushNarration(moveAnnouncer.describeMugginsClaim(scorer, points));
    afterStateChange(scorer);
  }

  function resetViewportToFit(animated: boolean): void {
    if (state === null) return;
    const cfg = layoutManager.getConfig();
    const chainLayout = computeChainLayout(state.chain, cfg.chainRegion);
    const bounds = computeChainBounds(chainLayout, cfg.chainRegion);
    const screenRect = regionToRect(cfg.chainRegion);
    viewport.clearUserControlled();
    // Reduced-motion: skip the tween and snap directly to the framed view.
    // The framing change still happens — it's just instantaneous.
    const tweenMs = animated && !prefersReducedMotion() ? AUTOFIT_TWEEN_MS : 0;
    viewport.fitTo(
      bounds,
      screenRect,
      AUTOFIT_PADDING,
      tweenMs,
      performance.now(),
    );
    lastFitChainLength = chainLayout.tiles.length;
  }

  /**
   * Reconcile UI state after the engine state changes. Drops the curtain
   * when the surface seat rotates (skipped in single-seat scenarios + when
   * `hotSeatHide` is off + via the `?nocurtain` URL flag). Always ends
   * with `tickAi()` so an AI seat picks up its turn without polling.
   */
  function maybeFinalizeRecord(): void {
    if (state === null || !state.matchOver || finalizedThisMatch) return;
    if (!recorder.getSession()) return;
    const winnerSeats = matchWinnerSeats(state);
    const result: 'win' | 'draw' = winnerSeats.length > 0 ? 'win' : 'draw';
    const record = recorder.finish(result, winnerSeats, state.scores);
    commitRecord(record);
    recorder.discard();
    finalizedThisMatch = true;
  }

  function afterStateChange(previousPovSeat: number): void {
    if (state === null) return;
    dispatcher.reset();

    if (state.handOver) {
      playGameOver();
      curtain = { kind: 'none' };
      maybeFinalizeRecord();
      refreshKeyboardChain();
      // narrateHandEndIfNeeded() in the render loop coalesces hand-end +
      // match-end with anything pending in the buffer and flushes once.
      return;
    }

    if (state.pendingMuggins) {
      curtain = { kind: 'none' };
      tickAi();
      refreshKeyboardChain();
      flushNarrations();
      return;
    }

    const nextPov = povSeat(state);
    if (nextPov !== previousPovSeat && shouldShowCurtain()) {
      curtain = { kind: 'down', forSeat: nextPov };
    } else {
      curtain = { kind: 'none' };
      layoutManager.setInputs({ localSeat: nextPov });
      tickAi();
    }
    refreshKeyboardChain();
    maybeNarrateTurn();
    // Combine the move/draw/pass/claim event with the turn change into one
    // polite live-region write so the SR reads both lines.
    flushNarrations();
    notifyTurnOwnershipChanged();
  }

  function shouldShowCurtain(): boolean {
    if (overrides.noCurtain) return false;
    if (!settingsManager.get().hotSeatHide) return false;
    // Only hot-seat (multi-human) needs the curtain. vs-AI and online keep
    // the surface on the human seat throughout.
    return opponent === 'local' && state !== null && state.playerCount >= 2;
  }

  function liftCurtain(): void {
    if (curtain.kind !== 'down') return;
    const seat = curtain.forSeat;
    curtain = { kind: 'none' };
    layoutManager.setInputs({ localSeat: seat });
    dispatcher.reset();
    tickAi();
    refreshKeyboardChain();
    maybeNarrateTurn();
    flushNarrations();
  }

  function rebuildAiPlayers(playerCount: PlayerCount, ruleSet: DominoRuleSet): void {
    for (const ai of aiPlayers.values()) ai.cancel();
    aiPlayers.clear();
    if (opponent !== 'ai') return;
    const enumerator = new AiMoveEnumerator(ruleSet);
    const evaluator = new Evaluator(ruleSet);
    const strategy = new DifficultyStrategy({ enumerator, evaluator, ruleSet });
    const difficulty = settingsManager.get().difficulty;
    const asyncHardSearcher = difficulty === 'hard' ? ensureHardWorker() : null;
    const human = humanSeat(playerCount);
    for (let s = 0; s < playerCount; s += 1) {
      if (s === human) continue;
      const ai = new AiPlayer({
        seat: s,
        strategy,
        minThinkMs: AI_MIN_THINK_MS,
        onAction: (action) => handleAiAction(s, action),
        asyncHardSearcher,
      });
      ai.setDifficulty(difficulty);
      aiPlayers.set(s, ai);
    }
  }

  function tickAi(): void {
    if (state === null) return;
    const seat = state.currentSeat;
    const currentAi = aiPlayers.get(seat);
    if (currentAi) currentAi.notifyTurn(state);
    if (state.pendingMuggins) {
      const scorerAi = aiPlayers.get(state.pendingMuggins.scorerSeat);
      if (scorerAi && scorerAi !== currentAi) scorerAi.notifyTurn(state);
    }
  }

  function handleAiAction(actorSeatIdx: number, action: AiAction): void {
    if (state === null) return;
    switch (action.kind) {
      case 'play': {
        const result = applyMove(state, actorSeatIdx, action.tile, action.armIndex, Date.now());
        state = result.state;
        recordTurn(action.armIndex, 'auto');
        recorder.recordPlay(actorSeatIdx, action.tile, action.armIndex, 'auto', result.pointsThisPlay);
        playPiecePlace();
        narratePlay(actorSeatIdx, action.tile, action.armIndex, result.pointsThisPlay);
        afterStateChange(actorSeatIdx);
        break;
      }
      case 'draw': {
        state = applyDraw(state, actorSeatIdx);
        recorder.recordDraw(actorSeatIdx);
        dispatcher.reset();
        const handSize = state.hands[actorSeatIdx]?.length ?? 0;
        pushNarration(moveAnnouncer.describeDraw(actorSeatIdx, handSize));
        // Draw doesn't advance the seat — re-poll the AI so it picks
        // again (likely a play this time; otherwise another draw/pass).
        refreshKeyboardChain();
        flushNarrations();
        tickAi();
        break;
      }
      case 'pass': {
        const previous = povSeat(state);
        state = applyPass(state, actorSeatIdx);
        recorder.recordPass(actorSeatIdx);
        pushNarration(moveAnnouncer.describePass(actorSeatIdx));
        afterStateChange(previous);
        break;
      }
      case 'claim-muggins': {
        const previous = povSeat(state);
        const points = state.pendingMuggins?.points ?? 0;
        state = claimMuggins(state, actorSeatIdx, Date.now());
        recorder.recordClaimMuggins(actorSeatIdx, points);
        pushNarration(moveAnnouncer.describeMugginsClaim(actorSeatIdx, points));
        afterStateChange(previous);
        break;
      }
    }
  }

  function buildRecorderSeed(
    mode: RecordedMode,
    variantId: VariantId,
    playerCount: PlayerCount,
    seed: number,
    ruleSet: DominoRuleSet,
  ): RecordedSeed {
    const settings = settingsManager.get();
    // Online: prefer the roster's server-asserted name for the local seat
    // (a signed-in player's handle) over the raw settings value, so the
    // recorded match names the local side the same way the opponent sees.
    const rosterName =
      mode === 'online'
        ? onlineRoster?.find((p) => p.seat === humanSeat(playerCount))?.name
        : undefined;
    return {
      mode,
      variant: variantId,
      playerCount,
      ...seedRuleFields(ruleSet),
      seed,
      difficulty: mode === 'ai' ? settings.difficulty : null,
      playerName: rosterName?.trim() || settings.playerName.trim() || 'Player',
      localSeat: mode === 'local' ? null : humanSeat(playerCount),
      roomCode: null,
      createdAt: Date.now(),
    };
  }

  function recordedModeForOpponent(o: Opponent): RecordedMode {
    return o === 'ai' ? 'ai' : o === 'online' ? 'online' : 'local';
  }

  // ─── In-game overlay (gear modal) + AFK assist ─────────────────────────────

  function shouldArmAfk(): boolean {
    const s = settingsManager.get();
    if (!s.afkAssistEnabled) return false;
    if (currentScene !== 'game') return false;
    if (state === null) return false;
    if (state.handOver || state.matchOver) return false;
    if (state.pendingMuggins !== null) return false;
    return state.currentSeat === povSeat(state);
  }

  function fireAfkHint(): void {
    if (!shouldArmAfk()) return;
    if (state === null) return;
    const seat = state.currentSeat;
    const hand = state.hands[seat] ?? [];
    // Pick the first legal play; the renderer will outline the tile in the rack.
    for (let i = 0; i < hand.length; i += 1) {
      const tile = hand[i];
      if (!tile) continue;
      if (hasLegalPlay(state.chain, [tile], state.forcedLeadTile)) {
        afkHintTileIndex = i;
        afkHintExpiresAt = Date.now() + 1600;
        playChime();
        setTimeout(() => {
          if (Date.now() >= afkHintExpiresAt) afkHintTileIndex = null;
        }, 1600);
        return;
      }
    }
  }

  function pokeAfk(): void {
    afkHintTileIndex = null;
    afkTimer?.poke();
  }

  function setupAfkForCurrentPhase(): void {
    if (afkTimer === null) return;
    if (shouldArmAfk()) afkTimer.arm();
    else {
      afkTimer.disarm();
      afkHintTileIndex = null;
    }
  }

  function buildOverlayContext(): GameOverlayContext {
    return {
      getSettingsRows: () =>
        buildInGameSettingsRows({
          ctx: {
            getSettings: () => settingsManager.get(),
            updateSettings: ctxMenu.updateSettings,
          },
          isOnline: () => network !== null,
          restartMatchOffline: () => {
            ActiveGameStore.clear();
            startNewGame();
            gameOverlay?.refreshSettings();
          },
        }),
      requestRestart: () => {
        if (network) {
          network.requestRestart();
        } else {
          ActiveGameStore.clear();
          startNewGame();
          gameOverlay?.close();
        }
      },
      acceptRestart: () => network?.acceptRestart(),
      declineRestart: () => network?.declineRestart(),
      requestResign: () => {
        if (network) {
          network.requestResign();
          return;
        }
        if (state === null) return;
        // Offline domino has no shared "winner by resignation" concept across
        // hands. Closest analogue: end the match as abandoned, archive the
        // record, return to play.
        if (recorder.getSession()) {
          const record = recorder.finish('abandoned', [], state.scores);
          commitRecord(record);
          recorder.discard();
        }
        ActiveGameStore.clear();
        gameOverlay?.close();
        navigateTo('play');
      },
      exitToMainMenu: () => navigateTo('menu'),
      isOnline: () => network !== null,
      getRestartState: () => ({
        pending: network?.isRestartPending() ?? false,
        canPropose: network?.canLocalProposeRestart() ?? true,
      }),
    };
  }

  function ensureOverlayInstance(): void {
    if (gameOverlay !== null) return;
    gameOverlay = new GameOverlay({
      context: buildOverlayContext(),
      strings: {
        title: t('gameOverlay.title'),
        settingsLabel: t('gameOverlay.settingsLabel'),
        settingsBackLabel: t('gameOverlay.settingsBackLabel'),
        settingsViewTitle: t('gameOverlay.settingsViewTitle'),
        restartLabel: t('gameOverlay.restartLabel'),
        restartHintOnline: t('gameOverlay.restartHintOnline'),
        restartHintAlreadyProposed: t('gameOverlay.restartHintAlreadyProposed'),
        restartHintPending: t('gameOverlay.restartHintPending'),
        returnToMenuLabel: t('gameOverlay.returnToMenuLabel'),
        returnToMenuConfirmTitle: t('gameOverlay.returnToMenuConfirmTitle'),
        returnToMenuConfirmYes: t('gameOverlay.returnToMenuConfirmYes'),
        returnToMenuConfirmNo: t('gameOverlay.returnToMenuConfirmNo'),
        giveUpLabel: t('gameOverlay.giveUpLabel'),
        giveUpConfirmTitle: t('gameOverlay.giveUpConfirmTitle'),
        giveUpConfirmYes: t('gameOverlay.giveUpConfirmYes'),
        giveUpConfirmNo: t('gameOverlay.giveUpConfirmNo'),
        close: t('gameOverlay.close'),
        settingsTabs: {
          gameplay: t('gameOverlay.settingsTabs.gameplay'),
          interface: t('gameOverlay.settingsTabs.interface'),
          audio: t('gameOverlay.settingsTabs.audio'),
        },
      },
    });
  }

  function toggleGameOverlay(): void {
    ensureOverlayInstance();
    gameOverlay?.toggle();
  }

  function mountGameSceneOverlay(): void {
    ensureOverlayInstance();
    if (afkTimer === null) {
      afkTimer = new AfkTimer(
        {
          isLocalTurn: () => shouldArmAfk(),
          onTimeout: () => fireAfkHint(),
        },
        settingsManager.get().afkTimeoutMs,
      );
    }
    if (escapeKeyHandler === null) {
      escapeKeyHandler = (e: KeyboardEvent) => {
        if (e.key !== 'Escape') return;
        if (currentScene !== 'game') return;
        e.preventDefault();
        e.stopPropagation();
        toggleGameOverlay();
      };
      window.addEventListener('keydown', escapeKeyHandler, true);
    }
    setupAfkForCurrentPhase();
    wireAfkPingButton();
    notifyTurnOwnershipChanged();
  }

  function unmountGameSceneOverlay(): void {
    if (gameOverlay?.isOpen()) gameOverlay.close();
    resultModal?.close();
    resultModal = null;
    resultModalKey = null;
    gameStateOverlay?.close();
    gameStateOverlay = null;
    afkTimer?.disarm();
    afkTimer = null;
    afkHintTileIndex = null;
    gearBounds = null;
    gearTooltip.set(null);
    if (escapeKeyHandler !== null) {
      window.removeEventListener('keydown', escapeKeyHandler, true);
      escapeKeyHandler = null;
    }
    teardownAfkPingButton();
  }

  function wireAfkPingButton(): void {
    if (network === null) return;
    if (pingButton !== null) return;
    pingButtonContainer = document.createElement('div');
    pingButtonContainer.style.position = 'fixed';
    pingButtonContainer.style.right = '1rem';
    pingButtonContainer.style.bottom = '1rem';
    pingButtonContainer.style.zIndex = '50';
    document.body.appendChild(pingButtonContainer);
    const button = new PingButton({
      label: '🔔',
      title: t('game.pingOpponent'),
      ariaLabel: t('game.pingOpponent'),
    });
    button.mount(pingButtonContainer);
    const tracker = network.getAfkPingTracker();
    const updateVisibility = (): void => {
      button.setVisible(tracker.canPing() && settingsManager.get().showAfkPingButton);
    };
    unsubAfkTracker = tracker.subscribe(updateVisibility);
    button.setOnClick(() => {
      network?.sendAfkPing();
      updateVisibility();
    });
    updateVisibility();
    pingButton = button;
  }

  function teardownAfkPingButton(): void {
    if (unsubAfkTracker !== null) {
      unsubAfkTracker();
      unsubAfkTracker = null;
    }
    pingButton?.dispose();
    pingButton = null;
    if (pingButtonContainer !== null) {
      pingButtonContainer.remove();
      pingButtonContainer = null;
    }
  }

  /**
   * Single funnel for "turn ownership may have changed" — called after every
   * engine mutation, every snapshot, and on game start. Drives the online AFK
   * tracker and the your-turn cue; both dedupe internally, so callers don't
   * have to know whether anything actually moved.
   */
  function notifyTurnOwnershipChanged(): void {
    if (state === null) return;
    const isOpponentTurn = state.currentSeat !== povSeat(state);
    network?.notifyTurnState(isOpponentTurn, state.currentSeat);
    turnSignal.update(isLocalTurnForSignal(state));
  }

  /**
   * Hot-seat is excluded: the human being told "your turn" is the one who just
   * played, sitting at the same device (and the curtain already handles the
   * hand-off). The cue is for turns that arrive while someone *else* was
   * acting — an AI seat thinking, or a remote seat.
   */
  function isLocalTurnForSignal(s: GameStateData): boolean {
    if (opponent === 'local') return false;
    if (s.handOver || s.matchOver) return false;
    // A Muggins window suspends the turn — the cue belongs to the move, and
    // fires when the window settles and the seat actually gets to play.
    if (s.pendingMuggins !== null) return false;
    return s.currentSeat === humanSeat(s.playerCount);
  }

  /** `null` (not just missing `userId`) means "anonymous, not addable." */
  function toOpponentIdentity(player: SeatedPlayer): OpponentIdentity | null {
    if (player.userId === undefined) return null;
    return { userId: player.userId, name: player.name, avatarUrl: player.avatarUrl ?? null };
  }

  /**
   * Every other seat at the table worth offering to add as a friend — winners
   * and non-winners alike. `[]` whenever there's no online session (hot-seat/
   * vs-AI never populate `onlineRoster`) or every other seat is anonymous;
   * `createAddOpponentControl` already treats an empty list as "render
   * nothing," so both cases collapse to the same no-control outcome.
   */
  function addOpponentCandidates(): readonly OpponentIdentity[] {
    if (network === null || onlineRoster === null) return [];
    const others: OpponentIdentity[] = [];
    for (const player of onlineRoster) {
      if (player.seat === localSeat) continue;
      const identity = toOpponentIdentity(player);
      if (identity !== null) others.push(identity);
    }
    return others;
  }

  /** Wraps the existing plain-text result body with the add-opponent control. */
  function buildMatchOverBody(bodyText: string): string | HTMLElement {
    const control = createAddOpponentControl(addOpponentCandidates());
    if (control === null) return bodyText;
    return el('div', { class: 'modal-body-with-avatar' }, [el('span', { text: bodyText }), control]);
  }

  /**
   * Drive the DOM hand/match-end result modal from the current engine state.
   * Idempotent across renders — only rebuilds the modal when the
   * stringly-keyed snapshot (matchOver + winners + blocked) actually changes,
   * so focus stays put while the body text is rewritten.
   */
  function resultSeatName(seat: number): string {
    return t('result.seatNumber', { number: seat + 1 });
  }

  function syncResultModal(s: typeof state): void {
    if (s === null || !s.handOver) {
      resultModal?.close();
      resultModal = null;
      resultModalKey = null;
      return;
    }
    const matchOver = s.matchOver;
    const { title, body: bodyText, key } = buildResultText(s, resultSeatName, tForAnnouncer);
    if (key === resultModalKey && resultModal !== null) return;

    // The add-opponent control only ever appears on the *match*-over modal —
    // the per-hand modal reopens on every hand of a multi-hand match (`key`
    // above changes each time), and a control rebuilt away on the very next
    // hand would look broken. `matchOver` fixes the winners/blocked values
    // for good, so the control's own add/added/error state survives untouched
    // for as long as this modal instance stays open.
    const body = matchOver ? buildMatchOverBody(bodyText) : bodyText;
    const primaryLabel = matchOver ? t('result.newMatch') : t('result.nextHand');
    const secondaryLabel = t('result.menu');
    const actions = [
      { label: secondaryLabel, onClick: applyResultSecondary },
      { label: primaryLabel, primary: true, onClick: applyResultPrimary },
    ];

    if (resultModal === null) {
      resultModal = openModal({ title, body, actions, dismissible: false });
      // Pull focus into the modal on first open so SR + keyboard users land
      // on the primary "Next hand / New match" button after a hand ends.
      // Microtask defers past the `document.body.append` so the button is
      // mounted before `.focus()` runs.
      const handle = resultModal;
      queueMicrotask(() => {
        if (resultModal === handle) handle.focusPrimary();
      });
    } else {
      resultModal.setTitle(title);
      resultModal.setBody(body);
      resultModal.setActions(actions);
    }
    resultModalKey = key;
  }

  /**
   * Mirror `network.overlay` into the DOM connection-status modal. Scoped to
   * the three connection states only — hand/match-end and rematch already
   * flow through `syncResultModal` off engine state, so those overlay values
   * are never surfaced here (avoids a second modal fighting for the screen).
   * Idempotent — safe to call every render.
   */
  function syncGameStateOverlay(): void {
    const snapshot = computeGameStateSnapshot();
    if (snapshot === null) {
      gameStateOverlay?.close();
      gameStateOverlay = null;
      return;
    }
    if (gameStateOverlay === null) {
      gameStateOverlay = createGameStateOverlay(buildGameStateOverlayLabels, {
        onAction: handleGameStateOverlayAction,
      });
    }
    gameStateOverlay.update(snapshot);
  }

  function computeGameStateSnapshot(): GameStateOverlaySnapshot | null {
    if (network === null) return null;
    // `resultModal` owns the screen once a hand/match ends (see
    // syncResultModal) — `openModal` has no singleton guard, so without this
    // a connection blip landing while that prompt is up would stack a second,
    // independent focus-trapped modal on top of it.
    if (state !== null && state.handOver) return null;
    const overlay = network.overlay;
    if (
      overlay !== 'opponent_disconnected' &&
      overlay !== 'reconnecting_self' &&
      overlay !== 'game_disconnected'
    ) {
      return null;
    }
    return {
      state: overlay,
      opponentName: network.opponentName,
      gracePeriodEndsAt: network.gracePeriodEndsAt,
    };
  }

  function handleGameStateOverlayAction(id: GameStateOverlayButtonId): void {
    if (id === 'leave' || id === 'return-to-menu') {
      navigateTo('menu');
    }
  }

  /** Hand/match-end → continue (next hand or new match). */
  function applyResultPrimary(): void {
    if (state === null) return;
    if (opponent === 'online') {
      if (state.matchOver) {
        network?.requestRematch();
      } else {
        network?.sendNextHand();
      }
      return;
    }
    if (state.matchOver) {
      startNewGame();
      return;
    }
    const nextSeed = Math.floor(Math.random() * 0x7fffffff);
    state = startNextHand(state, nextSeed);
    recorder.recordNextHand(nextSeed);
    const next = povSeat(state);
    if (shouldShowCurtain()) {
      curtain = { kind: 'down', forSeat: next };
    } else {
      layoutManager.setInputs({ localSeat: next });
      tickAi();
    }
    dispatcher.reset();
    resetChainTurns();
    lastFitChainLength = -1;
    resetViewportToFit(false);
    moveAnnouncer.resetTurnMemory();
    narratedHandResultKey = null;
    refreshKeyboardChain();
    maybeNarrateTurn();
    flushNarrations();
  }

  /** Hand/match-end → back to the play menu. */
  function applyResultSecondary(): void {
    navigateTo('play');
  }

  function startNewGame(): void {
    if (opponent === 'online') {
      // Server-authoritative — wait for the inaugural STATE_SYNC. Clear local
      // recorder bookkeeping; rendering pauses until applyOnlineSnapshot runs.
      recorder.discard();
      finalizedThisMatch = false;
      dispatcher.reset();
      resetChainTurns();
      curtain = { kind: 'none' };
      lastFitChainLength = -1;
      lastSnapshotHandNumber = -1;
      // `state` left as-is — it will be replaced by the first snapshot. The
      // rAF loop skips rendering when state is null (waiting), so leaving
      // stale state from a prior game is fine until the new STATE_SYNC lands.
      state = null;
      // The first snapshot decides whose turn it is; drop the previous
      // match's answer so an opening turn on our seat still sounds.
      turnSignal.reset();
      return;
    }
    const settings = settingsManager.get();
    const playerCount: PlayerCount = overrides.playerCount ?? settings.playerCount;
    const variantId: VariantId = overrides.variant ?? settings.variant;
    const ruleSet = applyRuleOverrides(getRuleSet(variantId), {
      pointGoal: settings.pointGoal,
      mugginsStrict: settings.mugginsStrict,
      opening13: settings.goatOpening13,
      baldFinish: settings.goatBaldFinish,
    });
    const seed = Math.floor(Math.random() * 0x7fffffff);
    state = createInitialState(ruleSet, playerCount, seed);

    // A new ActiveSession replaces any abandoned one in localStorage.
    recorder.discard();
    recorder.start(
      buildRecorderSeed(recordedModeForOpponent(opponent), variantId, playerCount, seed, ruleSet),
    );
    finalizedThisMatch = false;

    rebuildAiPlayers(playerCount, ruleSet);

    const initialPov = povSeat(state);
    layoutManager.setInputs({ playerCount, localSeat: initialPov });

    dispatcher.reset();
    resetChainTurns();
    curtain = { kind: 'none' };
    // Reset viewport for the new hand — frames the empty chain region.
    lastFitChainLength = -1;
    resetViewportToFit(false);
    moveAnnouncer.resetTurnMemory();
    narratedHandResultKey = null;
    // Hot-seat first-turn case: if the first visible seat isn't the local
    // viewer's seat, drop the curtain. (vs-AI keeps povSeat = human so
    // this branch never fires.)
    if (shouldShowCurtain() && state.currentSeat !== humanSeat(playerCount)) {
      curtain = { kind: 'down', forSeat: state.currentSeat };
    } else {
      tickAi();
    }
    refreshKeyboardChain();
    maybeNarrateTurn();
    flushNarrations();
    // Before the funnel below: a new match that opens on our seat should
    // sound, and without the reset it reads as "already our turn".
    turnSignal.reset();
    notifyTurnOwnershipChanged();
  }

  // Pointer IDs captured by an on-canvas UI button (reset/fit, action bar).
  // While a pointer is in this set, the dispatcher does NOT see its events —
  // preventing the button press from starting a pan or drag.
  const uiCapturedPointers = new Set<number>();

  new InputHandler({
    canvas,
    isRotated: () => layoutManager.getConfig().shouldRotateBoard,
    onInteraction: (event: PointerInteraction) => {
      if (currentScene !== 'game' || state === null) return;
      pokeAfk();
      // Gear-icon hit-test runs first so the overlay is reachable on top of
      // every other UI surface.
      const overGear = gearBounds !== null && gearHitTest({ x: event.canvasX, y: event.canvasY }, gearBounds);
      if (event.kind === 'move') gearHovered = overGear;
      else if (event.kind === 'leave' || event.kind === 'cancel') gearHovered = false;
      gearTooltip.set(gearHovered ? t('gameOverlay.openTooltip') : null);
      if (event.kind === 'up' && overGear) {
        gearHovered = false;
        gearTooltip.set(null);
        toggleGameOverlay();
        return;
      }

      // Hand/match-end is owned by the DOM result modal — its buttons fire
      // applyResultPrimary / applyResultSecondary directly. Swallow stray
      // pointer events on the canvas so they don't poke the selector
      // underneath while the modal is open.
      if (state.handOver) return;

      if (curtain.kind === 'down') {
        if (event.kind === 'up' && event.isPrimary) liftCurtain();
        return;
      }

      // On-canvas UI buttons: capture the pointer on down so the dispatcher
      // doesn't start a pan/drag underneath. Act on up; release on
      // up/leave/cancel.
      const overResetButton = hitTestResetButton(
        currentResetButton,
        event.canvasX,
        event.canvasY,
      );
      const overActionButton =
        currentActionButtons.length > 0 &&
        hitTestActionBar(currentActionButtons, event.canvasX, event.canvasY) !== null;
      const overBoneyard =
        currentBoneyardLayout !== null &&
        currentBoneyardDrawEnabled &&
        hitTestBoneyardStack(currentBoneyardLayout, event.canvasX, event.canvasY);
      const overUiButton = overResetButton || overActionButton || overBoneyard;

      // Track active pointers + hover position. Hover preview only fires
      // when no pointer is currently held (otherwise the user is panning,
      // pinching, or dragging — none of which want a hover overlay).
      if (event.kind === 'down') {
        activePointerIds.add(event.pointerId);
        hoverPosition = null;
      } else if (event.kind === 'up' || event.kind === 'cancel') {
        activePointerIds.delete(event.pointerId);
      } else if (event.kind === 'leave') {
        activePointerIds.delete(event.pointerId);
        hoverPosition = null;
      } else if (event.kind === 'move') {
        hoverPosition =
          activePointerIds.size === 0 ? { x: event.canvasX, y: event.canvasY } : null;
        const hoveredIdx =
          activePointerIds.size === 0 && !overUiButton
            ? isOverLocalHandTile(event.canvasX, event.canvasY)
            : null;
        const hoveredTile =
          hoveredIdx !== null
            ? currentLayout.rackPlacements[hoveredIdx]?.tile ?? null
            : null;
        const hoveredPlayable =
          hoveredTile !== null &&
          state !== null &&
          hasLegalPlay(state.chain, [hoveredTile], state.forcedLeadTile);
        canvas.style.cursor =
          overUiButton || hoveredPlayable ? 'pointer' : 'default';
      }

      if (event.kind === 'down' && overUiButton) {
        uiCapturedPointers.add(event.pointerId);
        return;
      }
      if (uiCapturedPointers.has(event.pointerId)) {
        if (event.kind === 'move') return;
        if (event.kind === 'leave' || event.kind === 'cancel') {
          uiCapturedPointers.delete(event.pointerId);
          return;
        }
        if (event.kind === 'up') {
          uiCapturedPointers.delete(event.pointerId);
          if (overResetButton) {
            resetViewportToFit(true);
            return;
          }
          if (overBoneyard) {
            performDraw();
            return;
          }
          if (overActionButton) {
            const hit = hitTestActionBar(currentActionButtons, event.canvasX, event.canvasY);
            if (hit === 'muggins') performMugginsClaim();
          }
          return;
        }
      }

      dispatcher.handle(event);
    },
    onWheel: (event: WheelInteraction) => {
      if (currentScene !== 'game' || state === null) return;
      const factor = event.deltaSign < 0 ? WHEEL_ZOOM_STEP : 1 / WHEEL_ZOOM_STEP;
      viewport.zoomAt({ x: event.canvasX, y: event.canvasY }, factor);
      viewport.markUserControlled();
    },
  });

  const menuRoot = document.getElementById('menu-root');
  if (menuRoot === null) throw new Error('#menu-root not found');


  function resumeActiveGame(): void {
    const session = recorder.getSession() ?? ActiveGameStore.peek();
    if (!session) return;
    if (session.seed.mode === 'online') {
      handleOnlineResume(session);
      return;
    }

    for (const ai of aiPlayers.values()) ai.cancel();
    aiPlayers.clear();

    const ruleSet = applyRuleOverrides(getRuleSet(session.seed.variant), seedRuleOverrides(session.seed));
    // Rebuild engine state by re-applying the recorded moves from scratch.
    let restored = createInitialState(ruleSet, session.seed.playerCount, session.seed.seed);
    resetChainTurns();
    for (const m of session.moves) {
      if (m.kind === 'play') {
        const result = applyMove(restored, m.seat, m.tile, m.armIndex, Date.now());
        restored = result.state;
        recordTurn(m.armIndex, m.turn);
      } else if (m.kind === 'draw') {
        restored = applyDraw(restored, m.seat);
      } else if (m.kind === 'pass') {
        restored = applyPass(restored, m.seat);
      } else if (m.kind === 'claim-muggins') {
        restored = claimMuggins(restored, m.seat, Date.now());
      } else if (m.kind === 'expire-muggins') {
        // Resume races the live timer — push expiry far in the future so the
        // engine takes the expire path regardless of real time.
        restored = expireMuggins(restored, Date.now() + 10_000);
      } else if (m.kind === 'next-hand') {
        restored = startNextHand(restored, m.seed);
        resetChainTurns();
      }
    }
    state = restored;
    finalizedThisMatch = state.matchOver;

    opponent = session.seed.mode === 'ai' ? 'ai' : 'local';
    pendingLocalSeat = session.seed.localSeat ?? 0;
    recorder.adopt(session);
    rebuildAiPlayers(session.seed.playerCount, ruleSet);

    const initialPov = povSeat(state);
    layoutManager.setInputs({ playerCount: session.seed.playerCount, localSeat: initialPov });
    dispatcher.reset();
    curtain = { kind: 'none' };
    lastFitChainLength = -1;
    resetViewportToFit(false);

    moveAnnouncer.resetTurnMemory();
    narratedHandResultKey = null;
    refreshKeyboardChain();
    resumingActiveGame = true;
    navigateTo('game');
  }

  // Set only while a resumeRoom() attempt is seated-but-waiting (a 3/4-seat
  // room not yet full) — cancelRoom() calls this before roomClient.cancel()
  // so resumeRoom() stops listening on this RoomClient instead of leaking
  // its onUpdate/onReconnectFail subscriptions into whatever this
  // long-lived RoomClient does next. See resumeRoom()'s `onSeatedWaiting`
  // doc comment (@bn-games/room-client) for why this can't be inferred from
  // roomClient's own state machine.
  let pendingResumeAbandon: (() => void) | null = null;

  /**
   * Opens the shared "Reconnecting…" progress modal, drives the T2
   * `resumeRoom()` flow helper to completion, and on failure hands off to
   * `showResumeFailure`. Shared by every resume entry point (menu, boot
   * hash/session, active-session button) so they all get the same feedback
   * instead of the boot path resolving silently on the main menu.
   */
  function attemptResume(roomCode: string, session: ActiveSession | null): void {
    const modal = openModal({
      title: t('online.resumeModal.reconnectingTitle'),
      body: t('online.resumeModal.reconnectingBody'),
      actions: [],
      dismissible: false,
    });

    void resumeRoom(roomClient, {
      roomCode,
      playerName: settingsManager.get().playerName,
      onSeatedWaiting: (abandon) => {
        // Domino-specific: a 3/4-seat room can genuinely wait minutes here.
        // Navigate to the online-join screen's waiting surface instead of
        // sitting behind the progress modal indefinitely.
        pendingResumeAbandon = abandon;
        modal.close();
        opponent = 'online';
        navigateTo('online-join');
      },
    }).then((outcome: ResumeOutcome) => {
      pendingResumeAbandon = null;
      modal.close();
      // "ready" navigation is already handled by the top-level
      // roomClient.onUpdate listener; "waiting" was already handled above by
      // onSeatedWaiting. Only "failed" needs a follow-up here.
      if (outcome.kind === 'failed') {
        showResumeFailure(outcome.cause, session, () => attemptResume(roomCode, session));
      }
    });
  }

  function showResumeFailure(
    cause: ResumeFailureCause,
    session: ActiveSession | null,
    onRetry: () => void,
  ): void {
    let modal: ModalHandle;
    const extraActions: ModalAction[] = [];
    if (session) {
      extraActions.push({
        label: t('online.resumeModal.viewSaved'),
        onClick: () => {
          modal.close();
          const record = recordFromActiveSession(session);
          pendingReplay = { record, returnTo: 'menu' };
          navigateTo('replay');
        },
      });
      extraActions.push({
        label: t('online.resumeModal.discard'),
        onClick: () => {
          modal.close();
          discardActiveSession(() => {
            if (router.getCurrentScene() === 'play') {
              router.hide();
              router.show('play');
            }
          });
        },
      });
    }
    modal = openResumeFailureModal({
      cause,
      signedIn: isLoggedIn(),
      onRetry,
      // Sign-in doesn't reclaim this seat — resolveSeat fixes identity at
      // first-seat time — but it should still pick up a freshly-signed-in
      // state for the next attempt, the same refresh fetchMe() does at boot.
      onSignIn: () => { void fetchMe(); },
      extraActions,
    });
  }

  function handleOnlineResume(session: ActiveSession): void {
    const seed = session.seed;
    if (!seed.roomCode) {
      // No server round-trip to fail — there's genuinely no room to rejoin,
      // which is the most honest of the four causes.
      showResumeFailure('ROOM_GONE', session, () => handleOnlineResume(session));
      return;
    }

    // Adopt up-front so the seed/id survive across the eventual STATE_SYNC.
    recorder.adopt(session);
    attemptResume(seed.roomCode, session);
  }

  function discardActiveSession(onConfirm?: () => void): void {
    const session = recorder.getSession() ?? ActiveGameStore.peek();
    if (!session) {
      onConfirm?.();
      return;
    }
    createDiscardModal({
      strings: {
        title: t('playMenu.discardModal.title'),
        body: t('playMenu.discardModal.body'),
        keep: t('playMenu.discardModal.keep'),
        discard: t('playMenu.discardModal.discard'),
      },
      onDiscard: () => {
        recorder.discard();
        onConfirm?.();
      },
      dismissible: true,
    });
  }

  const ctxMenu: MenuContext = {
    navigate: (scene) => navigateTo(scene),
    getSettings: () => settingsManager.get(),
    updateSettings: (partial) => {
      const before = settingsManager.get();
      settingsManager.update(partial);
      const after = settingsManager.get();
      if (after.theme !== before.theme) {
        applyTheme(after.theme);
        palette = themeToRenderPalette(THEMES[after.theme]);
      }
      if (after.sound !== before.sound) setMuted(!after.sound);
      if (after.language !== before.language) {
        void setLocale(after.language).then(() => router.remountCurrent());
      }
      if (after.difficulty !== before.difficulty) {
        for (const ai of aiPlayers.values()) ai.setDifficulty(after.difficulty);
      }
    },
    getOpponent: () => opponent,
    setOpponent: (next) => {
      opponent = next;
    },
    getPendingLocalSeat: () => pendingLocalSeat,
    setPendingLocalSeat: (next) => {
      pendingLocalSeat = next;
    },
    getPendingOnlineSeat: () => pendingOnlineSeat,
    setPendingOnlineSeat: (next) => {
      pendingOnlineSeat = next;
    },
    getPendingOnlineSeatCount: () => pendingOnlineSeatCount,
    setPendingOnlineSeatCount: (next) => {
      pendingOnlineSeatCount = next;
    },
    consumePendingJoinCode: () => {
      const code = pendingJoinCode;
      pendingJoinCode = null;
      return code;
    },
    roomClient,
    createRoom: () => {
      roomClient.createRoom(
        settingsManager.get().playerName,
        settingsManager.get().variant,
        pendingOnlineSeat,
        pendingOnlineSeatCount,
        settingsManager.get().verifiableDiceEnabled,
        roomRuleOptions(settingsManager.get()),
      );
    },
    joinRoom: (code) => {
      roomClient.joinRoom(code, settingsManager.get().playerName);
    },
    cancelRoom: () => {
      pendingResumeAbandon?.();
      pendingResumeAbandon = null;
      roomClient.cancel();
      onlineVariant = null;
      if (network) {
        network.detach();
        network = null;
      }
    },
    resumeSession: () => {
      const saved = sessionStore.load();
      if (!saved) return;
      attemptResume(saved.roomCode, recorder.getSession() ?? ActiveGameStore.peek());
    },
    getSavedSession: () => sessionStore.load(),
    getActiveSession: () => recorder.getSession() ?? ActiveGameStore.peek(),
    resumeActiveGame: () => resumeActiveGame(),
    discardActiveSession: (onConfirm) => discardActiveSession(onConfirm),
    historyStore,
    openReplay: (record, returnTo) => {
      pendingReplay = { record, returnTo };
      navigateTo('replay');
    },
  };

  const router = new MenuRouter<MenuContext>(menuRoot, ctxMenu);
  function quickStartVsAi(): void {
    const launch = (): void => {
      opponent = 'ai';
      pendingLocalSeat = 0;
      navigateTo('game');
    };
    ctxMenu.discardActiveSession(launch);
  }

  router.register('menu', () =>
    createMainMenuScreen({
      version: __APP_VERSION__,
      onQuickStart: () => quickStartVsAi(),
    }),
  );
  router.register('play', createPlayMenuScreen);
  router.register('settings', createSettingsScreen);
  router.register('rules', createRulesScreen);
  router.register('about', createAboutScreen);
  router.register('online-create', createOnlineCreateScreen);
  router.register('online-join', createOnlineJoinScreen);
  router.register('history', createHistoryListScreen);

  function modeForScene(scene: Scene): 'menu' | 'game' {
    // 'replay' shares the canvas with 'game' — the renderer just swaps.
    return scene === 'game' || scene === 'replay' ? 'game' : 'menu';
  }

  function navigateTo(scene: Scene): void {
    const enteringGame = scene === 'game' && currentScene !== 'game';
    const leavingGame = currentScene === 'game' && scene !== 'game';
    const enteringReplay = scene === 'replay' && currentScene !== 'replay';
    const leavingReplay = currentScene === 'replay' && scene !== 'replay';
    const leavingOnlineFlow =
      opponent === 'online' &&
      (currentScene === 'online-create' || currentScene === 'online-join') &&
      scene !== 'game' &&
      scene !== 'online-create' &&
      scene !== 'online-join';
    currentScene = scene;
    socialDock.showScene(scene);
    document.body.setAttribute('data-mode', modeForScene(scene));
    if (leavingReplay) replayScene.close();
    if (leavingOnlineFlow) {
      pendingResumeAbandon?.();
      pendingResumeAbandon = null;
      roomClient.cancel();
      if (network) {
        network.detach();
        network = null;
      }
      opponent = 'ai';
      onlineVariant = null;
    }
    if (leavingGame && opponent === 'online') {
      roomClient.cancel();
      if (network) {
        network.detach();
        network = null;
      }
      opponent = 'ai';
      onlineVariant = null;
    }
    if (leavingGame) {
      unmountGameSceneOverlay();
    }
    if (modeForScene(scene) === 'menu') {
      if (leavingGame) {
        for (const ai of aiPlayers.values()) ai.cancel();
      }
      router.show(scene);
    } else {
      router.hide();
      if (scene === 'game') {
        mountGameSceneOverlay();
      }
      if (enteringGame) {
        if (!resumingActiveGame) {
          startNewGame();
        }
        resumingActiveGame = false;
      } else if (enteringReplay && pendingReplay) {
        const { record, returnTo } = pendingReplay;
        pendingReplay = null;
        replayScene.open(record, returnTo);
      }
    }
  }

  window.addEventListener('keydown', (e) => {
    // Game-scene Escape is handled by the in-game overlay's capture-phase
    // listener (toggles the gear modal). Only fall through here for replay.
    if (e.key === 'Escape' && currentScene === 'replay') {
      replayScene.exit();
    }
  });

  // Fires on both sign-in and sign-out. Retrying the outbox is a no-op when
  // the transition was a sign-out (flushOutboxIfSignedIn checks authState);
  // remountCurrent() is a no-op unless a menu screen is showing (it reads
  // `router`'s own current-scene, not `currentScene` above), so this is safe
  // to fire unconditionally rather than gating it on the History screen.
  window.addEventListener(BN_AUTH_CHANGE, () => {
    flushOutboxIfSignedIn();
    router.remountCurrent();
  });

  // If URL overrides are present, jump straight into hot-seat — preserves
  // the M4-era solo-testing workflow. Otherwise land on the main menu.
  if (overrides.playerCount !== null || overrides.variant !== null) {
    opponent = 'local';
    navigateTo('game');
  } else {
    navigateTo('menu');
  }

  // URL hash takes precedence over a stored session. A #room=CODE link now
  // always resumes directly — the server resolves the seat (reclaimed
  // silently if it's ours, a fresh ROOM_JOINED if it's genuinely new, or a
  // genuine wait if the room isn't full — see attemptResume's
  // onSeatedWaiting) — rather than dropping the user on the Join screen.
  function resumeFromHashOrSession(): void {
    // No ActiveSession has necessarily been recorded yet at boot time — fall
    // back the same way ctxMenu.getActiveSession() does, so the failure
    // modal's View saved/Discard actions appear whenever there's something
    // to act on and are silently omitted otherwise.
    const session = recorder.getSession() ?? ActiveGameStore.peek();

    const hashEntry = readRoomHash();
    if (hashEntry) {
      // Seed a legacy token (if the link predates RESUME_ROOM) into the
      // room-keyed store so resume() can offer it to resolveSeat as one more
      // candidate — never as a trusted identity on its own.
      if (hashEntry.legacyToken) {
        sessionStore.saveFor(hashEntry.roomCode, hashEntry.legacyToken);
      }
      opponent = 'online';
      attemptResume(hashEntry.roomCode, session);
      return;
    }
    // Try to resume a stored online session on app load — if a tab refreshed
    // mid-game within the grace window, jump straight back to play.
    const storedEntry = sessionStore.load();
    if (storedEntry) {
      opponent = 'online';
      attemptResume(storedEntry.roomCode, session);
    }
  }

  /**
   * Everyone who launched the activity in the same channel shares one Discord
   * instance id, so it doubles as the room key — no lobby codes to exchange.
   * The Discord roster, not the stored setting, names the player here; the
   * setting is left untouched so the web build keeps the user's own choice.
   * The remaining room config comes from local settings, since the create
   * screen never renders inside the iframe. Resolves false when the handshake
   * failed, so startup can fall through to the normal menu instead of
   * dead-ending.
   */
  async function startDiscordActivity(): Promise<boolean> {
    const discord = await initDiscordActivity();
    if (discord === null) return false;

    connection.setTicket(discord.ticket);
    opponent = 'online';
    const settings = settingsManager.get();
    // The room becomes playable through the shared roomClient.onUpdate handler.
    roomClient.joinOrCreate(discord.instanceId, {
      playerName: discord.displayName,
      variant: settings.variant,
      seatCount: DISCORD_SEAT_COUNT,
      verifiable: settings.verifiableDiceEnabled,
      options: roomRuleOptions(settings),
    });
    return true;
  }

  // Inside Discord the instance room is authoritative — a saved session or a
  // share-link hash must not race it. Only one of the two paths ever runs.
  if (isDiscordActivity()) {
    void startDiscordActivity().then((joined) => {
      if (!joined) resumeFromHashOrSession();
    });
  } else {
    resumeFromHashOrSession();
  }

  function frame(_t: number): void {
    if (currentScene === 'game' && state !== null) {
      viewport.step(performance.now());
      render(state);
    } else if (currentScene === 'replay') {
      replayScene.render(ctx, palette);
    }
    requestAnimationFrame(frame);
  }

  /**
   * Render the dashed keyboard focus ring at the currently-focused item.
   * Hand tiles and action buttons sit in screen space; arm-slot rects are
   * world-space, so they need a viewport projection.
   */
  function drawFocusRingFor(
    item: FocusItem,
    rackPlacements: readonly RackTilePlacement[],
  ): void {
    if (item.kind === 'hand') {
      const placement = rackPlacements[item.tileIndex];
      if (placement) drawTileFocusRing(ctx, placement.rect, palette);
      return;
    }
    if (item.kind === 'armSlot') {
      if (item.armIndex === -1) {
        // Lead-only slot — ring around the chain centre.
        const cfg = layoutManager.getConfig();
        const cx = cfg.chainRegion.x + cfg.chainRegion.width / 2;
        const cy = cfg.chainRegion.y + cfg.chainRegion.height / 2;
        drawLeadFocusRing(ctx, { x: cx, y: cy }, LEAD_FOCUS_RING_RADIUS, palette);
        return;
      }
      if (item.rect === null) return;
      // World-space rect → project to screen via the viewport so the ring
      // tracks pan/zoom. Build a transient rect with the screen coords.
      const { x: wx, y: wy, long, short, orientation } = item.rect;
      const topLeftWorld = { x: wx, y: wy };
      const bottomRightWorld = {
        x: wx + (orientation === 'horizontal' ? long : short),
        y: wy + (orientation === 'horizontal' ? short : long),
      };
      const tl = viewport.worldToScreen(topLeftWorld);
      const br = viewport.worldToScreen(bottomRightWorld);
      const screenRect = {
        x: tl.x,
        y: tl.y,
        long: br.x - tl.x,
        short: br.y - tl.y,
        orientation: 'horizontal' as const,
      };
      drawTileFocusRing(ctx, screenRect, palette);
      return;
    }
    if (item.kind === 'action') {
      if (item.action === 'draw' && currentBoneyardLayout) {
        const b = currentBoneyardLayout;
        drawActionFocusRing(
          ctx,
          { x: b.x, y: b.y, w: b.width, h: b.height },
          palette,
        );
        return;
      }
      const btn = currentActionButtons.find((b) => b.id === item.action);
      if (btn) drawActionFocusRing(ctx, { x: btn.x, y: btn.y, w: btn.w, h: btn.h }, palette);
    }
  }

  function render(s: GameStateData): void {
    const cfg = layoutManager.getConfig();
    drawTable(ctx, CANVAS_WIDTH, CANVAS_HEIGHT, palette);
    drawChainRegionRecess(ctx, cfg.chainRegion, palette);

    // World pass: chain + arm highlights + drop-target glow.
    const chainLayout = computeChainLayout(s.chain, cfg.chainRegion, chainTurns);
    const dispatchSnap = dispatcher.snapshot();

    // Auto-fit when the chain grows (or shrinks via a new hand), unless
    // the user has manually panned/zoomed since the last fit.
    if (
      !viewport.isUserControlled() &&
      chainLayout.tiles.length !== lastFitChainLength
    ) {
      const bounds = computeChainBounds(chainLayout, cfg.chainRegion);
      const screenRect = regionToRect(cfg.chainRegion);
      // Reduced-motion: snap to the new framing instead of easing.
      const tweenMs = prefersReducedMotion() ? 0 : AUTOFIT_TWEEN_MS;
      viewport.fitTo(bounds, screenRect, AUTOFIT_PADDING, tweenMs, performance.now());
      lastFitChainLength = chainLayout.tiles.length;
    }

    ctx.save();
    // Clip to the chain region so pan/zoom can't bleed tiles into rack zones.
    ctx.beginPath();
    ctx.rect(cfg.chainRegion.x, cfg.chainRegion.y, cfg.chainRegion.width, cfg.chainRegion.height);
    ctx.clip();
    viewport.applyTo(ctx);
    drawChain(ctx, chainLayout, palette);
    if (dispatchSnap.draggingTile !== null) {
      const isDoubleTile = dispatchSnap.draggingTile[0] === dispatchSnap.draggingTile[1];
      if (dispatchSnap.canLead) {
        // Tile-shaped outline at chain center showing exactly where the lead
        // will land (horizontal for non-doubles, vertical for doubles).
        const cx = cfg.chainRegion.x + cfg.chainRegion.width / 2;
        const cy = cfg.chainRegion.y + cfg.chainRegion.height / 2;
        const leadSlot = leadTileSlot(cx, cy, isDoubleTile);
        drawDropSlotOutline(ctx, leadSlot, palette, { snap: dispatchSnap.snapToLead });
      }
      // Mid-chain: dispatcher already filtered out colliding slots. Render
      // each non-snapped slot faint; brighten the current snap target.
      for (const slot of dispatchSnap.candidateSlots) {
        const isSnap =
          dispatchSnap.snapSlot !== null &&
          dispatchSnap.snapSlot.armIndex === slot.armIndex &&
          dispatchSnap.snapSlot.turn === slot.turn;
        drawDropSlotOutline(ctx, slot.rect, palette, { snap: isSnap });
      }
    } else if (hoverPosition !== null) {
      // Idle hover preview: outline the legal drop slots for the tile the
      // pointer is currently over, so the player can survey placements
      // before committing to a drag. Uses the faint variant only — there's
      // no snap target without an active drag.
      const hoveredIndex = isOverLocalHandTile(hoverPosition.x, hoverPosition.y);
      if (hoveredIndex !== null) {
        const tile = currentLayout.rackPlacements[hoveredIndex]?.tile;
        if (tile) {
          const isDoubleTile = tile[0] === tile[1];
          if (dispatcher.canLeadWith(tile)) {
            const cx = cfg.chainRegion.x + cfg.chainRegion.width / 2;
            const cy = cfg.chainRegion.y + cfg.chainRegion.height / 2;
            drawDropSlotOutline(ctx, leadTileSlot(cx, cy, isDoubleTile), palette);
          }
          for (const slot of dispatcher.enumerateLegalSlots(tile)) {
            drawDropSlotOutline(ctx, slot.rect, palette);
          }
        }
      }
    }
    ctx.restore();

    // Screen pass: racks (south + opponents) — anchored above the world.
    const pov = povSeat(s);
    const seatPositions = orderSeatsForLocalView(cfg, pov);
    const localHand = s.hands[pov] ?? [];
    const actor = actorSeat(s);

    const southZone = cfg.seatZones.south;
    let rackPlacements: readonly RackTilePlacement[] = [];
    if (southZone) {
      drawHandZoneFrame(ctx, southZone, palette);
      rackPlacements = layoutLocalRack(southZone, localHand);
      // Mark the forced lead tile (Телефон opening) so the player knows
      // which tile to drag. Only relevant on the lead move + when it's the
      // local viewer's turn.
      const forcedIndex =
        s.forcedLeadTile !== null && s.chain.lead === null && s.currentSeat === pov
          ? localHand.findIndex(
              (t) => t[0] === s.forcedLeadTile![0] && t[1] === s.forcedLeadTile![1],
            )
          : -1;
      drawLocalRack(ctx, rackPlacements, palette, {
        draggingIndex: dispatchSnap.draggingTileIndex ?? undefined,
        forcedIndex: forcedIndex >= 0 ? forcedIndex : undefined,
      });
    }

    for (const [position, seatIndex] of seatPositions) {
      if (position === 'south') continue;
      const zone = cfg.seatZones[position];
      if (!zone || seatIndex === null) continue;
      const hand = s.hands[seatIndex] ?? [];
      drawHandZoneFrame(ctx, zone, palette);
      drawOpponentStack(ctx, zone, hand.length, palette);
    }

    // Carve a seam inside north + south rows at the inner edge of each
    // adjacent west/east column, so the corner sub-cells read as separate.
    drawHandZoneDividers(ctx, cfg.seatZones);

    // Per-seat name + score + tile count panels. Rendered after the frames
    // so the cards sit above the wood tray. Each card lives in the corner
    // cell owned by its seat (south → bottom-left, then west/north/east
    // clockwise around the table).
    for (const [position, seatIndex] of seatPositions) {
      const zone = cfg.seatZones[position];
      if (!zone || seatIndex === null) continue;
      const hand = s.hands[seatIndex] ?? [];
      drawPlayerInfo(ctx, position, buildPlayerInfo(seatIndex, hand.length, s, actor), palette);
    }

    // Drag ghost — drawn on top of racks so it stays visible when crossing them.
    if (dispatchSnap.draggingTile !== null && dispatchSnap.ghostScreen !== null) {
      const valid = dispatchSnap.snapSlot !== null || dispatchSnap.snapToLead;
      drawDragGhost(ctx, dispatchSnap.draggingTile, dispatchSnap.ghostScreen, palette, valid);
    }

    currentLayout = {
      rackPlacements,
      armEnds: chainLayout.armEnds,
      placedTiles: chainLayout.tiles,
      chainCenter: {
        x: cfg.chainRegion.x + cfg.chainRegion.width / 2,
        y: cfg.chainRegion.y + cfg.chainRegion.height / 2,
      },
    };

    currentResetButton = layoutResetButton(cfg.chainRegion);
    drawResetButton(ctx, currentResetButton, palette, viewport.isUserControlled());

    // Banner labels follow the engine's actor (current player or muggins
    // scorer) so vs-AI mode still reads "AI N's turn" when the human is
    // on the surface.
    const actorName = seatsFor(s.playerCount)[actor] ?? 'south';
    drawTurnBanner(ctx, { text: bannerForState(s, actorName, actor) }, palette);

    const seatHand = s.hands[s.currentSeat] ?? [];
    const hasPlay = hasLegalPlay(s.chain, seatHand, s.forcedLeadTile);
    // Draw/Pass only when the local viewer is the current player. In hot-seat
    // pov rotates with the actor; in vs-AI pov is the human, so the AI's
    // draw/pass turns get hidden buttons.
    const localIsCurrent = s.currentSeat === pov;
    const drawEnabled =
      !s.handOver &&
      curtain.kind === 'none' &&
      s.pendingMuggins === null &&
      localIsCurrent &&
      !hasPlay &&
      s.boneyard.length > 0 &&
      s.ruleSet.drawRule !== 'no-draw';
    // Auto-claim suppresses the button entirely — strict mode parks points in
    // `pendingMuggins` until the auto-claim path fires, which is instant
    // locally but lags by one server round-trip online; without this gate the
    // button flashes for ~1 RTT every time it would auto-resolve anyway.
    const mugginsVisible =
      s.pendingMuggins !== null
      && s.pendingMuggins.scorerSeat === pov
      && !settingsManager.get().autoClaimMuggins;
    const mugginsRemaining = s.pendingMuggins
      ? Math.max(
          0,
          (s.pendingMuggins.expiresAt - Date.now()) /
            s.ruleSet.mugginsClaimWindowMs,
        )
      : 0;

    // Boneyard pile lives at the bottom-right of the chain region as a
    // table element. Muggins (when surfaced) stacks above the max pile
    // footprint so its Y stays stable as the boneyard depletes.
    const showBoneyardCount = settingsManager.get().showBoneyardCount;
    currentBoneyardDrawEnabled = drawEnabled;
    currentBoneyardLayout = layoutBoneyardStack(cfg.chainRegion, s.boneyard.length);
    if (currentBoneyardLayout !== null) {
      drawBoneyardStack(
        ctx,
        currentBoneyardLayout,
        s.boneyard.length,
        palette,
        {
          glow: drawEnabled,
          showCount: showBoneyardCount,
          dimmed: !drawEnabled,
        },
      );
    }

    const bottomReserved =
      currentBoneyardLayout !== null
        ? BONEYARD_STACK_MAX_HEIGHT + BONEYARD_TO_MUGGINS_GAP
        : 0;
    currentActionButtons = layoutActionBar(
      {
        mugginsVisible,
        mugginsEnabled: mugginsVisible && mugginsRemaining > 0,
        mugginsRemaining,
      },
      t('actionBar.claimMuggins'),
      cfg.chainRegion,
      bottomReserved,
    );
    drawActionBar(ctx, currentActionButtons, palette, mugginsRemaining);

    // Keyboard focus ring — only drawn when the canvas itself owns focus.
    // Sits above tiles + action bar so the indicator stays visible no matter
    // what the cursor is on. Skipped during the curtain since the chain is
    // empty and the curtain renderer owns the screen.
    if (canvasFocused && curtain.kind === 'none') {
      const focused = kbdChain.currentItem();
      if (focused) drawFocusRingFor(focused, rackPlacements);
    }

    // AFK hint — pulse a soft ring on the suggested rack tile. Reduced-motion
    // collapses the sine oscillation to a static high-alpha ring so the hint
    // is still visible without the throb.
    if (afkHintTileIndex !== null && Date.now() < afkHintExpiresAt) {
      const placement = rackPlacements[afkHintTileIndex];
      if (placement) {
        const r = placement.rect;
        const w = r.orientation === 'horizontal' ? r.long : r.short;
        const h = r.orientation === 'horizontal' ? r.short : r.long;
        const alpha = prefersReducedMotion()
          ? 0.85
          : (() => {
              const tt = (Date.now() % 1200) / 1200;
              return Math.max(0.2, Math.min(0.85, 0.35 + 0.4 * Math.sin(tt * Math.PI * 2)));
            })();
        ctx.save();
        ctx.strokeStyle = palette.bannerBorder;
        ctx.globalAlpha = alpha;
        ctx.lineWidth = 3;
        ctx.strokeRect(r.x, r.y, w, h);
        ctx.restore();
      }
    }

    // Gear icon — top-right corner of the canvas.
    gearBounds = drawPauseGear(ctx, CANVAS_WIDTH, {
      fill: palette.bannerBg,
      // The soft ink, not the banner's hairline border: that one is ~1.4:1 against a
      // dark surround, and an icon needs 3:1 (WCAG 1.4.11).
      border: palette.textSecondary,
    }, { hover: gearHovered });

    // Muggins timeout — expire when the window elapses; auto-advance turn.
    // Online mode delegates this to the server (the authoritative timer).
    if (opponent !== 'online' && s.pendingMuggins && Date.now() > s.pendingMuggins.expiresAt) {
      const previousVisible = s.pendingMuggins.scorerSeat;
      state = expireMuggins(s, Date.now());
      recorder.recordExpireMuggins();
      pushNarration(moveAnnouncer.describeMugginsExpired());
      afterStateChange(previousVisible);
      return;
    }

    narrateHandEndIfNeeded();
    syncResultModal(s);
    syncGameStateOverlay();

    if (curtain.kind === 'down') {
      const seatName = seatsFor(s.playerCount)[curtain.forSeat] ?? 'south';
      drawCurtain(
        ctx,
        {
          title: t('curtain.title', { seat: seatLabelWithIndex(seatName, curtain.forSeat) }),
          subtitle: t('curtain.subtitle'),
          buttonLabel: t('curtain.ready'),
        },
        palette,
      );
    }

    // Auto-pass when the current player can neither play nor draw. Online
    // mode skips this — opponent hands aren't visible client-side (placeholder
    // tiles would lie about playability) and the server runs the same check.
    if (
      opponent !== 'online' &&
      !s.handOver &&
      curtain.kind === 'none' &&
      s.pendingMuggins === null &&
      !canCurrentPlayerAct(s)
    ) {
      const previousVisible = s.currentSeat;
      const seat = s.currentSeat;
      state = applyPass(s, seat);
      recorder.recordPass(seat);
      pushNarration(moveAnnouncer.describePass(seat));
      afterStateChange(previousVisible);
    }
  }

  function playerNameFor(seatIndex: number): string {
    const settings = settingsManager.get();
    // Online: pull names from the lobby roster the server broadcast on
    // ROOM_JOINED / GAME_START / RECONNECT_OK — including the local seat,
    // so a signed-in player reads the same server-asserted name (handle)
    // that opponents see, instead of the raw settings value. Falls through
    // to the settings/seat-number labels below when the roster hasn't
    // populated this seat yet.
    if (opponent === 'online') {
      const remote = roomClient.getInfo().players?.find((p) => p.seat === seatIndex)?.name;
      const trimmed = remote?.trim();
      if (trimmed) return trimmed;
    }
    // Local human always owns their configured name. Other seats fall back
    // to the seat tag ("P2", "P3", …) — same convention as the result modal.
    const isLocalHuman = opponent !== 'local' && seatIndex === humanSeat(
      (state?.playerCount ?? settings.playerCount) as PlayerCount,
    );
    const isHotSeatLocalSurface = opponent === 'local' && state !== null && seatIndex === povSeat(state);
    if (isLocalHuman || isHotSeatLocalSurface) {
      return settings.playerName.trim() || t('result.seatNumber', { number: seatIndex + 1 });
    }
    return t('result.seatNumber', { number: seatIndex + 1 });
  }

  function buildPlayerInfo(
    seatIndex: number,
    tileCount: number,
    s: GameStateData,
    activeSeat: number,
  ): PlayerInfo {
    return {
      name: playerNameFor(seatIndex),
      score: seatDisplayScore(s, seatIndex),
      tileCount,
      isActive: seatIndex === activeSeat && !s.handOver,
      scoreLabel: t(scoreLabelKey(s)),
      tilesLabel: t('playerInfo.tiles'),
    };
  }

  function actorNameFor(actorSeatIndex: number, s: GameStateData): string {
    // Online surfaces the configured player name; hot-seat / vs-AI use the
    // position-tagged "P2 (north)" label since opponents don't have per-seat
    // names. Mirrors the same decision in `bannerForState` for the turn-text.
    if (opponent === 'online') return playerNameFor(actorSeatIndex);
    const seatName = seatsFor(s.playerCount)[actorSeatIndex] ?? 'south';
    return seatLabelWithIndex(seatName, actorSeatIndex);
  }

  function lastActionPrefix(s: GameStateData): string {
    const last = s.lastAction;
    if (last === null) return '';
    const actor = actorNameFor(last.seat, s);
    switch (last.kind) {
      case 'play': {
        const a = last.tile[0];
        const b = last.tile[1];
        if (last.wentOut && last.points > 0) {
          return t('banner.lastPlayScoredWentOut', { actor, a, b, points: last.points });
        }
        if (last.wentOut) {
          return t('banner.lastPlayWentOut', { actor, a, b });
        }
        if (last.points > 0) {
          return t('banner.lastPlayScored', { actor, a, b, points: last.points });
        }
        return t('banner.lastPlay', { actor, a, b });
      }
      case 'draw':
        return t('banner.lastDraw', { actor });
      case 'pass':
        return t('banner.lastPass', { actor });
      case 'claim-muggins': {
        if (last.tile !== undefined) {
          const a = last.tile[0];
          const b = last.tile[1];
          return t('banner.lastClaimWithTile', { actor, points: last.points, a, b });
        }
        return t('banner.lastClaim', { actor, points: last.points });
      }
    }
  }

  function bannerForState(s: GameStateData, seat: DominoPlayer, seatIndex: number): string {
    if (s.matchOver) return t('banner.matchOver');
    if (s.handOver) return t('banner.handOver');
    // Online surfaces the configured player name so opponents read as
    // "Alex's turn" rather than "P2 (north)'s turn". Hot-seat / vs-AI keep
    // the position-tagged label since no per-seat name exists for opponents.
    const seatTag = opponent === 'online'
      ? playerNameFor(seatIndex)
      : seatLabelWithIndex(seat, seatIndex);
    const turnText = s.pendingMuggins
      ? t('banner.claimMuggins', { seat: seatTag })
      : t('banner.seatTurn', { seat: seatTag });
    const prefix = lastActionPrefix(s);
    return prefix.length > 0 ? `${prefix} ${turnText}` : turnText;
  }

  requestAnimationFrame(frame);
}

/**
 * Map canvas positions (south/west/north/east) to engine seat indices from
 * the local viewer's perspective. Local seat always sits south; the rest
 * fill west/north/east in clockwise rotation.
 */
function orderSeatsForLocalView(
  cfg: LayoutConfig,
  localSeat: number,
): Array<[DominoPlayer, number | null]> {
  const positions: readonly DominoPlayer[] =
    cfg.playerCount === 2
      ? ['south', 'north']
      : cfg.playerCount === 3
        ? ['south', 'west', 'north']
        : ['south', 'west', 'north', 'east'];
  const result: Array<[DominoPlayer, number | null]> = [];
  for (let i = 0; i < positions.length; i += 1) {
    const physical = positions[i]!;
    const seatIndex = (localSeat + i) % cfg.playerCount;
    result.push([physical, seatIndex]);
  }
  for (const p of ['south', 'west', 'north', 'east'] as const) {
    if (!result.find(([pos]) => pos === p)) result.push([p, null]);
  }
  return result;
}

void main();
